import { motion, AnimatePresence } from "framer-motion";
import React, {
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import {
  getAllConnectedNodeEdgesTypes,
  getGraphEdges,
  createNode,
  createEdge,
  deleteNode,
  deleteGraphNodes,
  exportGraphJSON,
  deleteEdge,
  updateNode,
  updateEdge,
  getTeamGraphs,
  getTeamMemberGraph,
  getConnectedNodeByType,
  getConnectedEdgeByType,
  getConnectedNodeByName,
  getConnectedEdgeByName,
  searchGraph,
  getGlobalGraph,
  getGraphViews,
  deleteGraphView,
  getGraphSources,
  getFilteredGraphView,
  favoriteGraphView,
  unfavoriteGraphView,
  getGraphCounts,
} from "../../../api/graph";
import { getProductDocIdData } from "../../../api/universal";

import { useRightSidebar } from "../../contexts/RightSidebarContext";
import { useLayout } from "../../contexts/LayoutContext";
import GraphTutorial from "./GraphTutorial";
import {
  Plus,
  X,
  Loader2,
  Network,
  FileText,
  Clock,
  UserCheck,
  Briefcase,
  User,
  Link2,
  MapPin,
  GraduationCap,
  Building2,
  Search,
  ShieldAlert,
  Building,
  GitCompareArrows,
  Globe,
  Ruler,
  Factory,
  Ship,
  Package,
  Info,
  Coins,
  BookOpen,
  Scale,
  Hash,
  ShieldCheck,
  Award,
  CalendarDays,
  Fingerprint,
  Pencil,
  Trash2,
} from "lucide-react";

import { THEMES, PANEL_KEYFRAMES } from "./config/graphThemes";
import { safeIcon, NODE_TYPE_OPTIONS } from "./config/graphIcons";
import { CIRCLE_RADIUS } from "./config/graphConstants";
import {
  FloatingPanel,
  ModalInput,
  ModalActions,
  ModalDropdown,
  ModalTextarea,
  ModalToggle,
} from "./components/GraphModals";
import useDark from "./hooks/useDark";
import GraphBottomBar from "./components/GraphBottomBar";
import LegendPanel from "./components/LegendPanel";
import SelectedItemsPanel from "./components/SelectedItemsPanel";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import { getTheme } from "../../Universal/useNavTheme";
import CreateEditViewModal from "./components/CreateEditViewModal";
import { useCompanyContext } from "../../../context";
import ForceGraph2D from "react-force-graph-2d";
import * as d3 from "d3";
import { renderToStaticMarkup } from "react-dom/server";

// ─────────────────────────────────────────────────────────────────────────────
// New graph visual system — this is the styling/icon layer from GraphNew.
// The functional MainGraph below still owns controls, CRUD, views, filters, etc.
// ─────────────────────────────────────────────────────────────────────────────
const VELBLUE = "#0EA5E9";

const ICON_MAP = {
  DOCUMENT: FileText,
  FILE_TEXT: FileText,
  DATETIME: Clock,
  DATE: CalendarDays,
  CALENDAR: CalendarDays,
  EMPLOYEE: UserCheck,
  USER_CHECK: UserCheck,
  OCCUPATION: Briefcase,
  BRIEFCASE: Briefcase,
  PERSON: User,
  USER: User,
  URL: Link2,
  LINK: Link2,
  LOCATION: MapPin,
  MAP_PIN: MapPin,
  SCHOOL: GraduationCap,
  WORKED_AT: Building2,
  SANCTION_LOOKUP_NAME: Search,
  SEARCH: Search,
  SANCTION_ENTITY: ShieldAlert,
  SHIELD_ALERT: ShieldAlert,
  SHIELD_CHECK: ShieldCheck,
  COMPANY: Building,
  BUILDING: Building,
  BUILDING_2: Building2,
  SIMILAR: GitCompareArrows,
  GIT_COMPARE: GitCompareArrows,
  WEBSITE: Globe,
  GLOBE: Globe,
  SIZE: Ruler,
  INDUSTRY: Factory,
  FACTORY: Factory,
  BILL_OF_LADING: Ship,
  BoL: Ship,
  SHIP: Ship,
  CONTAINER: Package,
  PACKAGE: Package,
  CRYPTO: Coins,
  COINS: Coins,
  LEDGER: BookOpen,
  BOOK_OPEN: BookOpen,
  COURT_FILING: Scale,
  SCALE: Scale,
  DOCKET_NUMBER: Hash,
  HASH: Hash,
  DOMAIN_CERTIFICATE: ShieldCheck,
  CERTIFICATE: Award,
  AWARD: Award,
  SERIAL_NUMBER: Fingerprint,
  FINGERPRINT: Fingerprint,
  INFO: Info,
};

const ICON_ALIASES = {
  person: "PERSON",
  people: "PERSON",
  official: "PERSON",
  politician: "PERSON",
  legislator: "PERSON",
  legislators: "PERSON",
  personnel: "USER_CHECK",
  employee: "EMPLOYEE",
  stakeholder: "USER_CHECK",
  group: "USER_CHECK",
  "population group": "USER_CHECK",

  country: "MAP_PIN",
  region: "MAP_PIN",
  location: "MAP_PIN",
  territories: "MAP_PIN",
  "administrative division": "MAP_PIN",

  event: "CALENDAR",
  action: "GIT_COMPARE",
  initiative: "GIT_COMPARE",
  service: "PACKAGE",
  inquiry: "SEARCH",
  directive: "FILE_TEXT",
  flag: "AWARD",

  organization: "BUILDING",
  "government body": "BUILDING",
  "news outlet": "GLOBE",
  "diplomatic facility": "BUILDING_2",
  "legislative body": "BUILDING",

  conflict: "SHIELD_ALERT",
  "military operation": "SHIELD_ALERT",
  "conflict phase": "SHIELD_ALERT",
  "target group": "SHIELD_ALERT",

  market: "COINS",
  commodity: "COINS",
  cost: "COINS",
  entity: "INFO",
  "term length": "CALENDAR",
  "election pattern": "CALENDAR",
};

function normalizeIconKey(icon) {
  return String(icon || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function resolveIconType(icon) {
  if (!icon) return "_DEFAULT";

  if (ICON_MAP[icon]) return icon;

  const normalized = normalizeIconKey(icon);
  const upperSnake = normalized.replace(/\s+/g, "_").toUpperCase();

  return (
    ICON_ALIASES[normalized] || (ICON_MAP[upperSnake] ? upperSnake : "_DEFAULT")
  );
}

const FALLBACK_ICON = Info;
const MAX_LABEL_CHARS = 28;
const COLLIDE_PER_CHAR = 3.5;
const COLLIDE_PADDING = 20;
const COLLIDE_MIN = 65;
const MAX_COLLIDE_RADIUS = MAX_LABEL_CHARS * COLLIDE_PER_CHAR + COLLIDE_PADDING;
const LINK_DISTANCE = Math.max(260, MAX_COLLIDE_RADIUS * 2.2);
const ICON_PX = 96;
const VIS_NODE_R = 10;
const VIS_NODE_R_ACTIVE = 13;
const VIS_PLUS_R = 5.5;
const VIS_PLUS_HIT_R = 12;
const SHOW_HOVER_CONNECT_HANDLE = false;

function getEndpointId(endpoint) {
  if (endpoint && typeof endpoint === "object")
    return endpoint.id || endpoint.name;
  return endpoint;
}

function getLinkId(link) {
  const source = getEndpointId(link?.source);
  const target = getEndpointId(link?.target);
  const label = link?.label || "UNKNOWN";

  return (
    link?.id ||
    link?.document_id ||
    `${source || "source"}-->${label}-->${target || "target"}`
  );
}

function logSelectedGraphNode(node, source = "unknown") {
  if (!node) return;
  console.log("[MainGraph] selected node", {
    source,
    node,
    id: node.id,
    name: node.name,
    document_id: node.document_id,
  });
}

function lucideToImage(IconComponent, color) {
  const markup = renderToStaticMarkup(
    React.createElement(IconComponent, {
      size: ICON_PX,
      color,
      strokeWidth: 2.5,
    }),
  );
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  return img;
}

function buildIconCache() {
  const cache = {};
  for (const [key, Component] of Object.entries(ICON_MAP)) {
    cache[key] = lucideToImage(Component, VELBLUE);
  }
  cache._DEFAULT = lucideToImage(FALLBACK_ICON, VELBLUE);
  return cache;
}

function getCanvasTheme(isDark) {
  if (isDark) {
    return {
      nodeFill: "rgba(18,28,46,0.92)",
      nodeStroke: "rgba(14,165,233,0.30)",
      nodeHoverFill: "rgba(14,165,233,0.22)",
      nodeHoverStroke: "rgba(14,165,233,0.80)",
      labelBg: "rgba(10,16,28,0.94)",
      labelStroke: "rgba(14,165,233,0.15)",
      labelText: "rgba(160,190,220,0.85)",
      labelHoverText: "rgba(220,235,250,1)",
      link: "rgba(14,165,233,0.35)",
      linkHover: "rgba(14,165,233,0.75)",
      particle: VELBLUE,
      velBlue: VELBLUE,
    };
  }

  return {
    nodeFill: "rgba(185,195,212,0.70)",
    nodeStroke: "rgba(110,128,152,0.50)",
    nodeHoverFill: "rgba(185,195,212,0.90)",
    nodeHoverStroke: "rgba(14,165,233,0.60)",
    labelBg: "rgba(255,255,255,0.92)",
    labelStroke: "rgba(130,145,168,0.40)",
    labelText: "rgba(30,45,65,0.80)",
    labelHoverText: "rgba(15,23,42,0.95)",
    link: "rgba(80,100,125,0.35)",
    linkHover: "rgba(40,55,75,0.60)",
    particle: "rgba(90,110,135,0.6)",
    velBlue: VELBLUE,
  };
}

function computeComponents(nodes, links) {
  const parent = new Map(nodes.map((n) => [n.id, n.id]));
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const l of links || []) {
    const source = getEndpointId(l.source);
    const target = getEndpointId(l.target);
    if (parent.has(source) && parent.has(target)) union(source, target);
  }
  const compId = new Map();
  for (const n of nodes) compId.set(n.id, find(n.id));
  return compId;
}

const VisualGraphRenderer2D = (props) => {
  const {
    fgRef,
    divRef,
    hoveredNodeRef,
    selectedNodeIdsRef,
    connectDragRef,
    selectionBoxRef,
    lastDragPosRef,
    suppressClickRef,
    cursorGraphPosRef,
    tooltipRafRef,
    zoomRef,
    needsZoomToFit,
    standaloneGraphPosRef,
    standaloneScreenPosRef,
    graphData,
    setGraphData,
    isDraggingFromPlus,
    setIsDraggingFromPlus,
    stopEngine,
    setStopEngine,
    dimensions,
    isLoading,
    isGraphEmpty,
    setConnectDrag,
    setSelectionBox,
    setConnectTargetNode,
    setNodeMenu,
    setContextMenu,
    setTooltipNode,
    setActiveNode,
    setPendingConnect,
    setShowNewNodeModal,
    setNewNodeModalPos,
    setNewNodeForm,
    setNewNodeSourceNode,
    setShowStandaloneModal,
    setStandaloneForm,
    setSelectedNodeIds,
    hasActiveFilter,
    visibleNodeIds,
    visibleLinkIds,
    activeLinkTypeFilter,
    degreeMap,
    degreeHighlight,
    searchMatchedNodes,
    searchMatchedLinks,
    nodeMenu,
    connectTargetNode,
    dark,
    showEdgeLabels,
    warmupTicks,
    linkTypes,
    handleFgNodeClick,
    handleFgNodeHover,
    openEditModal,
    openDeleteModal,
    activeNodeRef,
  } = props;

  const iconCacheRef = useRef(null);
  const canvasThemeRef = useRef(getCanvasTheme(dark));
  const viewportBoundsRef = useRef({
    minGx: -Infinity,
    maxGx: Infinity,
    minGy: -Infinity,
    maxGy: Infinity,
  });
  const pendingBackgroundClickRef = useRef(null);

  if (!iconCacheRef.current) iconCacheRef.current = buildIconCache();

  useEffect(() => {
    canvasThemeRef.current = getCanvasTheme(dark);
  }, [dark]);

  const findNodeAtGraphPos = useCallback(
    (gx, gy, excludeNodeId = null) => {
      if (!graphData?.nodes) return null;
      for (const n of graphData.nodes) {
        if (excludeNodeId && n.id === excludeNodeId) continue;
        const dx = (n.x || 0) - gx;
        const dy = (n.y || 0) - gy;
        if (Math.sqrt(dx * dx + dy * dy) < VIS_NODE_R_ACTIVE + 6) return n;
      }
      return null;
    },
    [graphData],
  );

  const hitTestPlusIcon = useCallback(
    (screenX, screenY) => {
      const node = hoveredNodeRef.current;
      if (!node || !fgRef.current) return null;
      const gp = fgRef.current.screen2GraphCoords(screenX, screenY);
      const angle = Math.atan2(gp.y - node.y, gp.x - node.x);
      const plusX = node.x + Math.cos(angle) * (VIS_NODE_R_ACTIVE + 5);
      const plusY = node.y + Math.sin(angle) * (VIS_NODE_R_ACTIVE + 5);
      const dx = gp.x - plusX;
      const dy = gp.y - plusY;
      return Math.sqrt(dx * dx + dy * dy) < VIS_PLUS_HIT_R ? node : null;
    },
    [hoveredNodeRef, fgRef],
  );

  const clearHoveredNode = useCallback(() => {
    if (!hoveredNodeRef.current) return;

    hoveredNodeRef.current = null;
    if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
    tooltipRafRef.current = requestAnimationFrame(() => {
      setTooltipNode(null);
    });
  }, [hoveredNodeRef, tooltipRafRef, setTooltipNode]);

  const syncHoveredNodeFromPointer = useCallback(
    (event, canvas) => {
      if (!fgRef.current || !canvas) return;

      const rect = canvas.getBoundingClientRect();
      const gxGy = fgRef.current.screen2GraphCoords(
        event.clientX - rect.left,
        event.clientY - rect.top,
      );
      const nodeUnderPointer = findNodeAtGraphPos(gxGy.x, gxGy.y);
      const current = hoveredNodeRef.current;

      if (nodeUnderPointer?.id === current?.id) return;

      hoveredNodeRef.current = nodeUnderPointer || null;
      if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
      tooltipRafRef.current = requestAnimationFrame(() => {
        setTooltipNode(nodeUnderPointer || null);
      });
    },
    [fgRef, findNodeAtGraphPos, hoveredNodeRef, tooltipRafRef, setTooltipNode],
  );

  useEffect(() => {
    if (isLoading || isGraphEmpty) return;
    const canvas = divRef.current?.querySelector("canvas");
    if (!canvas) return;

    const handlePointerDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const isSelectionModifier = e.metaKey || e.ctrlKey;
      const isAdditiveSelection = true;

      setContextMenu(null);
      setNodeMenu(null);
      pendingBackgroundClickRef.current = null;

      const gp = fgRef.current.screen2GraphCoords(sx, sy);
      const nodeUnder = findNodeAtGraphPos(gp.x, gp.y);
      if (nodeUnder) return;

      if (SHOW_HOVER_CONNECT_HANDLE) {
        const hitPlusNode = hitTestPlusIcon(sx, sy);
        if (hitPlusNode) {
          e.stopImmediatePropagation();
          e.preventDefault();
          const ds = {
            sourceNode: hitPlusNode,
            currentScreenPos: { x: e.clientX, y: e.clientY },
          };
          connectDragRef.current = ds;
          setConnectDrag({ ...ds });
          setIsDraggingFromPlus(true);
          return;
        }
      }

      if (isSelectionModifier) {
        e.stopImmediatePropagation();
        e.preventDefault();
        selectionBoxRef.current = {
          startX: e.clientX,
          startY: e.clientY,
          currentX: e.clientX,
          currentY: e.clientY,
          moved: false,
          additive: isAdditiveSelection,
        };
        setSelectionBox({ ...selectionBoxRef.current });
        return;
      }

      pendingBackgroundClickRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
      };
    };

    const handlePointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (fgRef.current) {
        cursorGraphPosRef.current = fgRef.current.screen2GraphCoords(
          e.clientX - rect.left,
          e.clientY - rect.top,
        );
      }

      syncHoveredNodeFromPointer(e, canvas);

      if (pendingBackgroundClickRef.current) {
        const dx = e.clientX - pendingBackgroundClickRef.current.startX;
        const dy = e.clientY - pendingBackgroundClickRef.current.startY;
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
          pendingBackgroundClickRef.current.moved = true;
        }
      }

      if (connectDragRef.current) {
        e.preventDefault();
        connectDragRef.current.currentScreenPos = {
          x: e.clientX,
          y: e.clientY,
        };
        setConnectDrag({ ...connectDragRef.current });
        const gp = cursorGraphPosRef.current;
        setConnectTargetNode(
          findNodeAtGraphPos(
            gp.x,
            gp.y,
            connectDragRef.current.sourceNode.id,
          ) || null,
        );
        return;
      }

      if (selectionBoxRef.current) {
        e.preventDefault();
        const dx = e.clientX - selectionBoxRef.current.startX;
        const dy = e.clientY - selectionBoxRef.current.startY;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3)
          selectionBoxRef.current.moved = true;
        selectionBoxRef.current.currentX = e.clientX;
        selectionBoxRef.current.currentY = e.clientY;
        setSelectionBox({ ...selectionBoxRef.current });
      }
    };

    const handlePointerUp = (e) => {
      if (connectDragRef.current) {
        const { sourceNode } = connectDragRef.current;
        const rect = canvas.getBoundingClientRect();
        const gp = fgRef.current.screen2GraphCoords(
          e.clientX - rect.left,
          e.clientY - rect.top,
        );
        const targetNode = findNodeAtGraphPos(gp.x, gp.y, sourceNode.id);
        if (targetNode) {
          setPendingConnect({
            source: sourceNode,
            target: targetNode,
            relationship: linkTypes[0] || "CONNECTED_TO",
            context: "",
            isPrivate: false,
            screenX: e.clientX,
            screenY: e.clientY,
          });
        } else {
          setNewNodeSourceNode(sourceNode);
          setShowNewNodeModal(true);
          setNewNodeModalPos({ x: e.clientX, y: e.clientY });
          setNewNodeForm({
            name: "",
            type: "PERSON",
            relationship: linkTypes[0] || "CONNECTED_TO",
            context: "",
            edgeContext: "",
            isPrivate: false,
          });
        }
        connectDragRef.current = null;
        setConnectDrag(null);
        setConnectTargetNode(null);
        setIsDraggingFromPlus(false);
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 50);
        return;
      }

      if (selectionBoxRef.current) {
        if (selectionBoxRef.current.moved) {
          const box = selectionBoxRef.current;
          const rect = canvas.getBoundingClientRect();
          const minSX = Math.min(box.startX, box.currentX) - rect.left;
          const maxSX = Math.max(box.startX, box.currentX) - rect.left;
          const minSY = Math.min(box.startY, box.currentY) - rect.top;
          const maxSY = Math.max(box.startY, box.currentY) - rect.top;
          const topLeft = fgRef.current.screen2GraphCoords(minSX, minSY);
          const bottomRight = fgRef.current.screen2GraphCoords(maxSX, maxSY);
          const newSel = box.additive
            ? new Set(selectedNodeIdsRef.current)
            : new Set();

          const boxHitNodes = [];
          const boxDocumentIds = new Set();
          const directNodeIds = new Set();

          (graphData?.nodes || []).forEach((n) => {
            if (
              n.x >= topLeft.x &&
              n.x <= bottomRight.x &&
              n.y >= topLeft.y &&
              n.y <= bottomRight.y
            ) {
              boxHitNodes.push(n);
              const docId = n.document_id || n.doc_id || n.cardId;
              if (docId) boxDocumentIds.add(docId);
              else if (n.id) directNodeIds.add(n.id);
            }
          });

          const boxSelectedNodes = [];

          (graphData?.nodes || []).forEach((n) => {
            const docId = n.document_id || n.doc_id || n.cardId;
            const shouldSelect =
              (docId && boxDocumentIds.has(docId)) || directNodeIds.has(n.id);

            if (shouldSelect) {
              newSel.add(n.id);
              boxSelectedNodes.push(n);
            }
          });

          if (boxSelectedNodes.length > 0) {
            console.log("[MainGraph] box selected document clusters", {
              hit_count: boxHitNodes.length,
              selected_node_count: boxSelectedNodes.length,
              additive: Boolean(box.additive),
              hit_nodes: boxHitNodes,
              selected_nodes: boxSelectedNodes,
              document_ids: Array.from(boxDocumentIds),
            });
          }

          selectedNodeIdsRef.current = newSel;
          setSelectedNodeIds(newSel);

          if (!box.additive) {
          }
        }

        selectionBoxRef.current = null;
        setSelectionBox(null);
        pendingBackgroundClickRef.current = null;
        return;
      }

      if (pendingBackgroundClickRef.current) {
        // Plain background clicks and pans should not mutate selection.
        // Large selections are intentionally cleared from the selected-items panel.
        pendingBackgroundClickRef.current = null;
      }
    };

    const handleContextMenu = (e) => {
      e.preventDefault();

      if (!fgRef.current) return;

      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const gp = fgRef.current.screen2GraphCoords(sx, sy);
      const nodeUnder = findNodeAtGraphPos(gp.x, gp.y);

      if (nodeUnder) {
        setNodeMenu(null);
        setContextMenu({
          x: e.clientX,
          y: e.clientY,
          item: nodeUnder,
          kind: "node",
        });
        return;
      }

      setContextMenu(null);
    };

    const handleDblClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left;
      const sy = e.clientY - rect.top;
      const gp = fgRef.current.screen2GraphCoords(sx, sy);
      const nodeUnder = findNodeAtGraphPos(gp.x, gp.y);
      if (nodeUnder) {
        activeNodeRef.current = nodeUnder;
        setActiveNode(nodeUnder);
        return;
      }
      standaloneGraphPosRef.current = { x: gp.x, y: gp.y };
      standaloneScreenPosRef.current = { x: e.clientX, y: e.clientY };
      setStandaloneForm({
        name: "",
        type: "PERSON",
        context: "",
        isPrivate: false,
      });
      setShowStandaloneModal(true);
    };

    const handlePointerLeave = () => {
      clearHoveredNode();
    };

    canvas.addEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    canvas.addEventListener("dblclick", handleDblClick);
    canvas.addEventListener("contextmenu", handleContextMenu);
    canvas.addEventListener("pointerleave", handlePointerLeave);
    canvas.addEventListener("mouseleave", handlePointerLeave);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      canvas.removeEventListener("pointerdown", handlePointerDown, {
        capture: true,
      });
      canvas.removeEventListener("dblclick", handleDblClick);
      canvas.removeEventListener("contextmenu", handleContextMenu);
      canvas.removeEventListener("pointerleave", handlePointerLeave);
      canvas.removeEventListener("mouseleave", handlePointerLeave);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [
    isLoading,
    isGraphEmpty,
    graphData,
    hitTestPlusIcon,
    findNodeAtGraphPos,
    linkTypes,
    syncHoveredNodeFromPointer,
    clearHoveredNode,
  ]);

  const handleFgZoom = useCallback(
    ({ k }) => {
      zoomRef.current = k;
      if (fgRef.current) {
        const margin = 60;
        const topLeft = fgRef.current.screen2GraphCoords(-margin, -margin);
        const bottomRight = fgRef.current.screen2GraphCoords(
          dimensions.width + margin,
          dimensions.height + margin,
        );
        viewportBoundsRef.current = {
          minGx: topLeft.x,
          maxGx: bottomRight.x,
          minGy: topLeft.y,
          maxGy: bottomRight.y,
        };
      }
    },
    [dimensions.width, dimensions.height],
  );

  const handleFgNodeDrag = useCallback(
    (node) => {
      if (isDraggingFromPlus) return;
      const isMulti =
        selectedNodeIdsRef.current.has(node.id) &&
        selectedNodeIdsRef.current.size > 1;
      if (isMulti && lastDragPosRef.current) {
        const dx = node.x - lastDragPosRef.current.x;
        const dy = node.y - lastDragPosRef.current.y;
        graphData?.nodes?.forEach((n) => {
          if (n.id !== node.id && selectedNodeIdsRef.current.has(n.id)) {
            n.x = (n.x || 0) + dx;
            n.y = (n.y || 0) + dy;
            n.fx = n.x;
            n.fy = n.y;
          }
        });
      }
      if (isMulti) lastDragPosRef.current = { x: node.x, y: node.y };
      node.fx = node.x;
      node.fy = node.y;
    },
    [isDraggingFromPlus, graphData],
  );

  const handleFgNodeDragEnd = useCallback(
    (node) => {
      if (isDraggingFromPlus) return;
      node.fx = node.x;
      node.fy = node.y;
      lastDragPosRef.current = null;
    },
    [isDraggingFromPlus],
  );

  const handleFgEngineStop = useCallback(() => {
    if (!stopEngine) {
      setStopEngine(true);
      if (graphData?.nodes) {
        graphData.nodes.forEach((n) => {
          if (n.x != null) n.fx = n.x;
          if (n.y != null) n.fy = n.y;
        });
      }
      if (needsZoomToFit.current && fgRef.current) {
        needsZoomToFit.current = false;
        fgRef.current.zoomToFit(0, 60);
      }
    }
  }, [stopEngine, graphData, setStopEngine, needsZoomToFit, fgRef]);

  const bubbleMenu = null;

  const nodeCanvasObject = useCallback(
    (node, ctx, globalScale) => {
      const cache = iconCacheRef.current;
      const tc = canvasThemeRef.current;
      if (!tc) return;

      const active = hoveredNodeRef.current || activeNodeRef.current;
      const isActive = active && active.id === node.id;
      const isSelected = selectedNodeIdsRef.current.has(node.id);
      const isSearchMatch =
        searchMatchedNodes.size > 0 && searchMatchedNodes.has(node.name);
      const isDegreeHit =
        degreeHighlight > 0 && (degreeMap[node.id] || 0) >= degreeHighlight;
      const isVisible = visibleNodeIds.has(node.id);
      const isDimmedByFilter = hasActiveFilter && !isVisible && !isActive;
      const isManual = node.group === "manual" || node.manual;

      const isNeighbour =
        active &&
        !isActive &&
        graphData?.links?.some((l) => {
          const source = getEndpointId(l.source);
          const target = getEndpointId(l.target);
          return (
            (source === active.id && target === node.id) ||
            (target === active.id && source === node.id)
          );
        });
      const dimmed =
        !isSelected &&
        ((active && !isActive && !isNeighbour) || isDimmedByFilter);

      const vb = viewportBoundsRef.current;
      if (!isActive && !isSelected && !isSearchMatch) {
        if (
          node.x < vb.minGx ||
          node.x > vb.maxGx ||
          node.y < vb.minGy ||
          node.y > vb.maxGy
        )
          return;
      }

      const zoomFactor = Math.max(
        0.55,
        Math.min(1.4, 1 / Math.sqrt(globalScale)),
      );
      const R = (isActive ? VIS_NODE_R_ACTIVE : VIS_NODE_R) * zoomFactor;
      const ICON = (isActive ? 17 : 12) * zoomFactor;
      const iconType = resolveIconType(
        node.iconType || node.icon || node.group,
      );

      ctx.save();
      if (dimmed) ctx.globalAlpha = 0.18;

      if (isSearchMatch && !isDimmedByFilter) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, R + 7, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(14,165,233,0.10)";
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = "rgba(14,165,233,0.65)";
        ctx.stroke();
      }

      if (isDegreeHit && !isDimmedByFilter) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, R + 6, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(250,204,21,0.10)";
        ctx.fill();
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(250,204,21,0.50)";
        ctx.stroke();
      }

      if (isSelected) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, R + 7, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(14,165,233,0.08)";
        ctx.fill();
        ctx.beginPath();
        ctx.arc(node.x, node.y, R + 5, 0, Math.PI * 2);
        ctx.setLineDash([2.5, 2]);
        ctx.lineWidth = 1.15;
        ctx.strokeStyle = tc.nodeHoverStroke;
        ctx.stroke();
        ctx.setLineDash([]);
      }

      ctx.beginPath();
      ctx.arc(node.x, node.y, R, 0, Math.PI * 2);
      ctx.fillStyle = isActive ? tc.nodeHoverFill : tc.nodeFill;
      ctx.fill();
      ctx.strokeStyle = isSelected
        ? tc.nodeHoverStroke
        : isActive || isNeighbour
          ? tc.nodeHoverStroke
          : tc.nodeStroke;
      ctx.lineWidth = isSelected
        ? 1.25
        : isActive
          ? 0.8
          : isNeighbour
            ? 0.5
            : 0.3;
      ctx.stroke();

      const img = cache[iconType] || cache._DEFAULT;
      if (img && img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, node.x - ICON / 2, node.y - ICON / 2, ICON, ICON);
      } else {
        ctx.beginPath();
        ctx.arc(node.x, node.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = tc.velBlue;
        ctx.fill();
      }

      if (
        SHOW_HOVER_CONNECT_HANDLE &&
        isActive &&
        !(nodeMenu?.node?.id === node.id)
      ) {
        const cgp = cursorGraphPosRef.current || { x: node.x + 1, y: node.y };
        const angle = Math.atan2(cgp.y - node.y, cgp.x - node.x);
        const px = node.x + Math.cos(angle) * (R + 4);
        const py = node.y + Math.sin(angle) * (R + 4);
        ctx.beginPath();
        ctx.arc(px, py, VIS_PLUS_R + 1, 0, Math.PI * 2);
        ctx.fillStyle = tc.nodeFill;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(px, py, VIS_PLUS_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(14,165,233,0.18)";
        ctx.fill();
        ctx.lineWidth = 0.7;
        ctx.strokeStyle = tc.nodeHoverStroke;
        ctx.stroke();
        const arm = VIS_PLUS_R * 0.5;
        ctx.beginPath();
        ctx.moveTo(px - arm, py);
        ctx.lineTo(px + arm, py);
        ctx.moveTo(px, py - arm);
        ctx.lineTo(px, py + arm);
        ctx.lineWidth = 0.9;
        ctx.strokeStyle = tc.velBlue;
        ctx.stroke();
      }

      if (globalScale >= 0.4) {
        const rawLabel = node.name || "";
        const maxChars = globalScale < 0.8 ? 16 : MAX_LABEL_CHARS;
        const label =
          rawLabel.length > maxChars
            ? rawLabel.slice(0, maxChars - 2) + "…"
            : rawLabel;
        const fs = Math.min(3.5, Math.max(2.2, 14 / globalScale));
        ctx.font = `500 ${fs}px "Poppins", sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        const tw = ctx.measureText(label).width;
        const px = 2.8;
        const py = 1.2;
        const lx = node.x - tw / 2 - px;
        const ly = node.y + R + 2.5;
        const lw = tw + px * 2;
        const lh = fs + py * 2;
        const cr = 2;
        ctx.beginPath();
        ctx.moveTo(lx + cr, ly);
        ctx.lineTo(lx + lw - cr, ly);
        ctx.quadraticCurveTo(lx + lw, ly, lx + lw, ly + cr);
        ctx.lineTo(lx + lw, ly + lh - cr);
        ctx.quadraticCurveTo(lx + lw, ly + lh, lx + lw - cr, ly + lh);
        ctx.lineTo(lx + cr, ly + lh);
        ctx.quadraticCurveTo(lx, ly + lh, lx, ly + lh - cr);
        ctx.lineTo(lx, ly + cr);
        ctx.quadraticCurveTo(lx, ly, lx + cr, ly);
        ctx.closePath();
        ctx.fillStyle = tc.labelBg;
        ctx.fill();
        ctx.strokeStyle = tc.labelStroke;
        ctx.lineWidth = 0.15;
        ctx.stroke();
        ctx.fillStyle = isActive ? tc.labelHoverText : tc.labelText;
        ctx.fillText(label, node.x, ly + py);
      }

      ctx.restore();
    },
    [
      graphData,
      hasActiveFilter,
      visibleNodeIds,
      degreeMap,
      degreeHighlight,
      searchMatchedNodes,
      nodeMenu,
      cursorGraphPosRef,
    ],
  );

  const linkColorFn = useCallback(
    (link) => {
      const tc = canvasThemeRef.current;
      if (!tc) return "transparent";

      const source = getEndpointId(link.source);
      const target = getEndpointId(link.target);
      const label = link.label || "UNKNOWN";
      const linkId = getLinkId(link);
      const linkVisible = !hasActiveFilter || visibleLinkIds.has(linkId);

      if (!linkVisible)
        return dark ? "rgba(14,165,233,0.03)" : "rgba(0,0,0,0.03)";
      if (activeLinkTypeFilter.size > 0 && activeLinkTypeFilter.has(label)) {
        return tc.linkHover;
      }

      const active = hoveredNodeRef.current || activeNodeRef.current;
      if (!active) return tc.link;
      return source === active.id || target === active.id
        ? tc.linkHover
        : tc.link;
    },
    [
      dark,
      hasActiveFilter,
      visibleLinkIds,
      activeLinkTypeFilter,
      hoveredNodeRef,
      activeNodeRef,
    ],
  );

  const linkWidthFn = useCallback(
    (link) => {
      const source = getEndpointId(link.source);
      const target = getEndpointId(link.target);
      const label = link.label || "UNKNOWN";
      const linkId = getLinkId(link);
      const linkVisible = !hasActiveFilter || visibleLinkIds.has(linkId);

      if (!linkVisible) return 0.3;
      if (searchMatchedLinks.size > 0 && searchMatchedLinks.has(link.id))
        return 3;
      if (activeLinkTypeFilter.size > 0 && activeLinkTypeFilter.has(label))
        return 3;

      const active = hoveredNodeRef.current || activeNodeRef.current;
      return active && (source === active.id || target === active.id)
        ? 2.5
        : dark
          ? 1.2
          : 1;
    },
    [
      dark,
      hasActiveFilter,
      visibleLinkIds,
      searchMatchedLinks,
      activeLinkTypeFilter,
      hoveredNodeRef,
      activeNodeRef,
    ],
  );

  const particleWidthFn = useCallback(
    (link) => {
      const active = hoveredNodeRef.current || activeNodeRef.current;
      if (!active) return 0;
      const source = getEndpointId(link.source);
      const target = getEndpointId(link.target);
      return source === active.id || target === active.id ? 2.5 : 0;
    },
    [hoveredNodeRef, activeNodeRef],
  );

  const linkCanvasObject = useCallback(
    (link, ctx, globalScale) => {
      const tc = canvasThemeRef.current;
      if (!tc || !link?.label) return;

      const s = link.source;
      const target = link.target;
      if (!s || !target || s.x == null || target.x == null) return;

      const active = hoveredNodeRef.current || activeNodeRef.current;
      const sourceId = getEndpointId(link.source);
      const targetId = getEndpointId(link.target);
      const isHighlighted =
        active && (sourceId === active.id || targetId === active.id);

      // Keep labels globally available when the bottom-bar toggle is on,
      // but always allow labels for hovered/active links.
      const shouldRenderLabel = showEdgeLabels || isHighlighted;
      if (!shouldRenderLabel) return;

      // The old threshold of 1.5 made labels disappear at normal graph zooms.
      // Use a lower threshold for general labels, and keep highlighted labels
      // visible a bit further out.
      const minScale = isHighlighted ? 0.45 : 0.65;
      if (globalScale < minScale) return;

      const mx = (s.x + target.x) / 2;
      const my = (s.y + target.y) / 2;
      const text = String(link.label).replace(/_/g, " ").toLowerCase();

      // Match the node label sizing exactly so edge labels do not visually overpower nodes.
      const fs = Math.min(3.5, Math.max(2.2, 14 / globalScale));
      const px = 2.8;
      const py = 1.2;
      const radius = 2;

      ctx.save();
      ctx.font = `500 ${fs}px "Poppins", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const tw = ctx.measureText(text).width;
      const x = mx - tw / 2 - px;
      const y = my - fs / 2 - py;
      const w = tw + px * 2;
      const h = fs + py * 2;

      ctx.globalAlpha = isHighlighted ? 0.88 : 0.58;
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fillStyle = tc.labelBg;
      ctx.fill();

      // Keep edge labels quieter than node labels: filled chip, no outline.
      ctx.globalAlpha = isHighlighted ? 0.88 : 0.56;
      ctx.fillStyle = isHighlighted ? tc.labelHoverText : tc.labelText;
      ctx.fillText(text, mx, my + 0.05);
      ctx.restore();
    },
    [showEdgeLabels, hoveredNodeRef, activeNodeRef],
  );

  const linkLineDashFn = useCallback((link) => {
    // Manual/user-created relationships are rendered as solid links.
    // Generated/backend relationships are dashed so users can distinguish them
    // without relying on unavailable fields like current_case.
    if (link.manual) return null;
    return [5, 3];
  }, []);

  const nodePointerAreaPaintFn = useCallback(
    (node, color, ctx, globalScale) => {
      // Keep the ForceGraph hit area centered on the same point as the visual node.
      // The old rectangular hit area was much larger than the rendered node and
      // extended farther below the center, which made hover/click feel offset when
      // nearby node hit regions overlapped.
      const hitRadius = Math.max(
        16,
        Math.min(24, 18 / Math.sqrt(globalScale || 1)),
      );

      ctx.beginPath();
      ctx.arc(node.x, node.y, hitRadius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    },
    [],
  );

  const d3Init = useCallback(
    (sim) => {
      const nodes = graphData?.nodes || [];
      const links = graphData?.links || [];
      const n = nodes.length;
      const isLarge = n > 100;

      sim.force(
        "charge",
        d3
          .forceManyBody()
          .strength(isLarge ? -600 : -1000)
          .theta(0.9),
      );
      sim.force(
        "link",
        d3
          .forceLink()
          .id((d) => d.id)
          .distance(LINK_DISTANCE)
          .strength(0.3),
      );
      sim.force(
        "collide",
        d3
          .forceCollide()
          .radius((d) => {
            const chars = Math.min((d.name || "").length, MAX_LABEL_CHARS);
            return Math.max(
              chars * COLLIDE_PER_CHAR + COLLIDE_PADDING,
              COLLIDE_MIN,
            );
          })
          .strength(1)
          .iterations(4),
      );

      const compId = computeComponents(nodes, links);
      const comps = Array.from(new Set(compId.values()));
      const cols = Math.max(1, Math.ceil(Math.sqrt(comps.length)));
      const spacing = LINK_DISTANCE * 0.6;
      const anchors = new Map();
      comps.forEach((cid, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        anchors.set(cid, {
          x: (col - (cols - 1) / 2) * spacing,
          y: (row - (Math.ceil(comps.length / cols) - 1) / 2) * spacing,
        });
      });
      sim.force(
        "componentX",
        d3.forceX((d) => anchors.get(compId.get(d.id))?.x || 0).strength(0.03),
      );
      sim.force(
        "componentY",
        d3.forceY((d) => anchors.get(compId.get(d.id))?.y || 0).strength(0.03),
      );
      sim.force("center", d3.forceCenter(0, 0).strength(1));
      sim.velocityDecay(0.4);
      sim.alphaDecay(0.015);
      return sim;
    },
    [graphData],
  );

  return (
    <div className="relative z-10 h-full w-full" data-tutorial="graph-canvas">
      {bubbleMenu}
      <ForceGraph2D
        ref={fgRef}
        graphData={graphData}
        backgroundColor="#00000000"
        nodeId="id"
        nodeLabel={() => ""}
        autoPauseRedraw={false}
        width={dimensions.width}
        height={dimensions.height}
        minZoom={0.2}
        maxZoom={8}
        onZoom={handleFgZoom}
        enableNodeDrag={!isDraggingFromPlus}
        d3ForceInit={d3Init}
        onNodeClick={handleFgNodeClick}
        onNodeHover={handleFgNodeHover}
        onNodeDrag={handleFgNodeDrag}
        onNodeDragEnd={handleFgNodeDragEnd}
        nodeCanvasObject={nodeCanvasObject}
        onEngineStop={handleFgEngineStop}
        warmupTicks={warmupTicks}
        cooldownTicks={Infinity}
        linkColor={linkColorFn}
        linkWidth={linkWidthFn}
        linkLineDash={linkLineDashFn}
        nodePointerAreaPaint={nodePointerAreaPaintFn}
        linkDirectionalParticles={3}
        linkDirectionalParticleColor={() =>
          canvasThemeRef.current?.particle || VELBLUE
        }
        linkDirectionalParticleWidth={particleWidthFn}
        linkDirectionalParticleSpeed={() => 0.006}
        linkCanvasObjectMode={() => "after"}
        linkCanvasObject={linkCanvasObject}
      />
    </div>
  );
};

const MainGraph = () => {
  const dark = useDark();
  const t = dark ? THEMES.dark : THEMES.light;

  const datePickerTheme = useMemo(
    () =>
      dark
        ? {
            textPrimary: "rgba(226,232,240,0.9)",
            textSecondary: "rgba(148,163,184,0.7)",
            textMuted: "rgba(148,163,184,0.4)",
            inputBg: "rgba(17,24,39,0.60)",
            inputBorder: "rgba(14,165,233,0.12)",
            activeBg: "rgba(14,165,233,0.15)",
            activeBorder: "rgba(14,165,233,0.30)",
            activeText: "rgba(14,165,233,0.9)",
            hoverBg: "rgba(17,24,39,0.80)",
            hoverBorder: "rgba(56,189,248,0.25)",
            hoverBtnShadow: "0 0 12px rgba(28,157,234,0.15)",
            divider: "rgba(14,165,233,0.15)",
            dropBg: "rgba(11,15,20,0.95)",
            dropBorder: "rgba(14,165,233,0.15)",
            dropShadow:
              "0 8px 32px rgba(0,0,0,0.4), 0 0 1px rgba(14,165,233,0.2)",
            pillBg: "rgba(17,24,39,0.60)",
            pillBorder: "rgba(14,165,233,0.12)",
            clearText: "rgba(248,113,113,0.9)",
            clearBg: "rgba(239,68,68,0.10)",
            clearBgHover: "rgba(239,68,68,0.20)",
            clearBorder: "rgba(239,68,68,0.20)",
          }
        : {
            textPrimary: "rgba(15,23,42,0.9)",
            textSecondary: "rgba(71,85,105,0.8)",
            textMuted: "rgba(148,163,184,0.6)",
            inputBg: "rgba(241,245,249,0.8)",
            inputBorder: "rgba(203,213,225,0.5)",
            activeBg: "rgba(14,165,233,0.10)",
            activeBorder: "rgba(14,165,233,0.30)",
            activeText: "rgba(2,132,199,1)",
            hoverBg: "rgba(241,245,249,1)",
            hoverBorder: "rgba(14,165,233,0.25)",
            hoverBtnShadow: "0 0 10px rgba(14,165,233,0.10)",
            divider: "rgba(203,213,225,0.4)",
            dropBg: "rgba(255,255,255,0.98)",
            dropBorder: "rgba(203,213,225,0.5)",
            dropShadow: "0 8px 24px rgba(0,0,0,0.10), 0 0 1px rgba(0,0,0,0.05)",
            pillBg: "rgba(241,245,249,0.8)",
            pillBorder: "rgba(203,213,225,0.4)",
            clearText: "rgba(220,38,38,0.9)",
            clearBg: "rgba(239,68,68,0.08)",
            clearBgHover: "rgba(239,68,68,0.15)",
            clearBorder: "rgba(239,68,68,0.15)",
          },
    [dark],
  );

  const { isRightOpen, setIsRightOpen, setRightComponent } = useRightSidebar();
  const { isLeftOpen, rightWidth, LEFT_WIDTH } = useLayout();

  const is3D = false;
  const setIs3D = () => {};
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchEntry, setSearchEntry] = useState("");
  const [connectedTypes, setConnectedTypes] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [searchTitle, setSearchTitle] = useState(null);

  const fgRef = useRef();
  const zoomRef = useRef(1);
  const [nodePosition, setNodePosition] = useState(null);
  const [stopEngine, setStopEngine] = useState(false);
  const needsZoomToFit = useRef(true);
  const [graphData, setGraphData] = useState(null);
  const [backendTotals, setBackendTotals] = useState({ nodes: 0, edges: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [tooltipNode, setTooltipNode] = useState(null);
  const tooltipRafRef = useRef(null);
  const divRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isGraphEmpty, setIsGraphEmpty] = useState(false);
  const hoveredNodeRef = useRef(null);
  const iconImgCacheRef = useRef(new Map());
  const activeNodeRef = useRef(null);
  const [activeNode, setActiveNode] = useState(null);
  const connectDragRef = useRef(null);
  const [connectDrag, setConnectDrag] = useState(null);
  const suppressClickRef = useRef(false);

  const selectedNodeIdsRef = useRef(new Set());
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const [isDownloadingSelection, setIsDownloadingSelection] = useState(false);
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const selectionBoxRef = useRef(null);
  const [selectionBox, setSelectionBox] = useState(null);
  const lastDragPosRef = useRef(null);

  const [showNewNodeModal, setShowNewNodeModal] = useState(false);
  const [newNodeModalPos, setNewNodeModalPos] = useState({ x: 0, y: 0 });
  const [newNodeSourceNode, setNewNodeSourceNode] = useState(null);
  const [newNodeForm, setNewNodeForm] = useState({
    name: "",
    type: "PERSON",
    relationship: "",
    context: "",
    edgeContext: "",
    isPrivate: false,
  });
  const [isDraggingFromPlus, setIsDraggingFromPlus] = useState(false);
  const cursorGraphPosRef = useRef({ x: 0, y: 0 });
  const [connectTargetNode, setConnectTargetNode] = useState(null);
  const nodeMenu = null;
  const setNodeMenu = useCallback(() => {}, []);
  const [showStandaloneModal, setShowStandaloneModal] = useState(false);
  const [standaloneForm, setStandaloneForm] = useState({
    name: "",
    type: "PERSON",
    context: "",
    isPrivate: false,
  });
  const standaloneGraphPosRef = useRef(null);
  const standaloneScreenPosRef = useRef({ x: 0, y: 0 });
  const [pendingConnect, setPendingConnect] = useState(null);
  const [connectionSourceNode, setConnectionSourceNode] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);

  const [barSearch, setBarSearch] = useState("");
  const [searchMatchedNodes, setSearchMatchedNodes] = useState(new Set());
  const [searchMatchedLinks, setSearchMatchedLinks] = useState(new Set());
  const [activeTypeFilter, setActiveTypeFilter] = useState(new Set());
  const [activeLinkTypeFilter, setActiveLinkTypeFilter] = useState(new Set());
  const [showEdgeLabels, setShowEdgeLabels] = useState(true);
  const [manualOnly, setManualOnly] = useState(false);
  const [hideOrphans, setHideOrphans] = useState(false);
  const [degreeHighlight, setDegreeHighlight] = useState(0);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const tutorialGraphSnapshotRef = useRef(null);

  const [graphSourceLabel, setGraphSourceLabel] = useState("My Graph");
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeGraphUuid, setActiveGraphUuid] = useState(null);
  const [graphLimit, setGraphLimit] = useState(100);
  const [graphDateFrom, setGraphDateFrom] = useState("");
  const [graphDateTo, setGraphDateTo] = useState("");
  const limitDebounceTimer = useRef(null);
  const graphSourceMeta = useRef({ kind: "my_graph" });

  const [graphFavorites, setGraphFavorites] = useState([]);
  const [customViews, setCustomViews] = useState([]);
  const [showCreateViewModal, setShowCreateViewModal] = useState(false);
  const [editingView, setEditingView] = useState(null);
  const [graphSources, setGraphSources] = useState([]);
  const [graphTargetTypes, setGraphTargetTypes] = useState([]);

  const [toasts, setToasts] = useState([]);
  const toastTimersRef = useRef({});
  const pendingDeleteTimersRef = useRef({});
  const pendingDeletePayloadsRef = useRef({});

  const { companyKey } = useCompanyContext();

  // FIX: track whether this is the initial mount so the date effect
  // doesn't fire a redundant second fetch before the first one resolves.
  const isMountedFetchRef = useRef(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setConnectionSourceNode(null);
        setPendingConnect(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    setConnectDrag(null);
    connectDragRef.current = null;
    setNodeMenu(null);
    setSelectionBox(null);
    selectionBoxRef.current = null;
    setConnectTargetNode(null);
    setIsDraggingFromPlus(false);
    needsZoomToFit.current = true;
    setTimeout(() => {
      if (fgRef.current) fgRef.current.zoomToFit?.(400, 60);
    }, 600);
  }, [is3D]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.page === "/graph") {
        tutorialGraphSnapshotRef.current = graphData
          ? { nodes: [...graphData.nodes], links: [...graphData.links] }
          : null;
        setShowTutorial(true);
      }
    };
    window.addEventListener("navbar:start-tutorial", handler);
    return () => window.removeEventListener("navbar:start-tutorial", handler);
  }, [graphData]);

  const fetchBackendCounts = useCallback(() => {
    const meta = graphSourceMeta.current;
    let scope = "",
      userUuid = "",
      scopeValue = "",
      filters = null;
    if (meta.kind === "global") scope = "global";
    else if (meta.kind === "team") {
      scope = "team";
      userUuid = meta.uuid || "";
    } else if (meta.kind === "node" || meta.kind === "node_type") {
      scope = "node_type";
      scopeValue = meta.value || "";
    } else if (meta.kind === "link" || meta.kind === "edge_type") {
      scope = "edge_type";
      scopeValue = meta.value || "";
    } else if (meta.kind === "custom_view" && meta.view?.filters) {
      scope = "custom_view";
      filters = meta.view.filters;
    }
    getGraphCounts(scope, userUuid, scopeValue, filters)
      .then((r) => {
        try {
          const d = typeof r?.data === "string" ? JSON.parse(r.data) : r?.data;
          if (d?.status === 200)
            setBackendTotals({
              nodes: d.total_nodes || 0,
              edges: d.total_edges || 0,
            });
        } catch {}
      })
      .catch((err) => {
        console.error("[MainGraph] fetchBackendCounts failed:", err);
      });
  }, []);

  useEffect(() => {
    getTeamGraphs()
      .then((res) => {
        try {
          if (res.data?.team) setTeamMembers(res.data.team);
        } catch {}
      })
      .catch((err) => {
        console.error("[MainGraph] getTeamGraphs failed:", err);
      });
  }, []);

  const loadGraphViewsData = useCallback(() => {
    getGraphViews()
      .then((res) => {
        try {
          if (res.data?.favorites) setGraphFavorites(res.data.favorites);
          if (res.data?.views) setCustomViews(res.data.views);
        } catch {}
      })
      .catch((err) => {
        console.error("[MainGraph] getGraphViews failed:", err);
      });
  }, []);

  useEffect(() => {
    loadGraphViewsData();
    const handler = () => loadGraphViewsData();
    window.addEventListener("sidebar:refresh-graph-views", handler);
    return () =>
      window.removeEventListener("sidebar:refresh-graph-views", handler);
  }, [loadGraphViewsData]);

  useEffect(() => {
    const handler = () => {
      setEditingView(null);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:graph-open-create-modal", handler);
    return () =>
      window.removeEventListener("sidebar:graph-open-create-modal", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const view = e.detail?.view;
      if (!view) return;
      setEditingView(view);
      setShowCreateViewModal(true);
    };
    window.addEventListener("sidebar:graph-open-edit-modal", handler);
    return () =>
      window.removeEventListener("sidebar:graph-open-edit-modal", handler);
  }, []);

  useEffect(() => {
    getGraphSources()
      .then((res) => {
        try {
          if (res.data?.sources) setGraphSources(res.data.sources);
          if (res.data?.target_types)
            setGraphTargetTypes(res.data.target_types);
        } catch {}
      })
      .catch((err) => {
        console.error("[MainGraph] getGraphSources failed:", err);
      });
  }, []);

  const pushToast = useCallback((message, undoPayload, type = "connection") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, undoPayload, type }]);
    toastTimersRef.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete toastTimersRef.current[id];
    }, 10000);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    if (toastTimersRef.current[id]) {
      clearTimeout(toastTimersRef.current[id]);
      delete toastTimersRef.current[id];
    }
  }, []);

  const degreeMap = useMemo(() => {
    const dm = {};

    (graphData?.links || []).forEach((l) => {
      const s = getEndpointId(l.source);
      const t = getEndpointId(l.target);

      if (s) dm[s] = (dm[s] || 0) + 1;
      if (t) dm[t] = (dm[t] || 0) + 1;
    });

    return dm;
  }, [graphData]);

  const maxDegree = useMemo(() => {
    const vals = Object.values(degreeMap);
    return vals.length ? Math.max(...vals) : 0;
  }, [degreeMap]);

  const typeCounts = useMemo(() => {
    const counts = {};
    (graphData?.nodes || []).forEach((n) => {
      const tp = n.icon || n.group || "UNKNOWN";
      counts[tp] = (counts[tp] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [graphData]);

  const linkTypeCounts = useMemo(() => {
    const counts = {};

    (graphData?.links || []).forEach((link) => {
      const label = link.label || "UNKNOWN";
      counts[label] = (counts[label] || 0) + 1;
    });

    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [graphData]);

  const graphStats = useMemo(() => {
    const nodes = graphData?.nodes?.length || 0;
    const edges = graphData?.links?.length || 0;
    const manualNodes = (graphData?.nodes || []).filter(
      (n) => n.group === "manual",
    ).length;
    const manualEdges = (graphData?.links || []).filter((l) => l.manual).length;
    const orphanCount = (graphData?.nodes || []).filter(
      (n) => !degreeMap[n.id],
    ).length;
    return { nodes, edges, manualNodes, manualEdges, orphanCount };
  }, [graphData, degreeMap]);

  const visibleNodeIds = useMemo(() => {
    if (!graphData?.nodes) return new Set();

    let nodes = graphData.nodes;
    const hasLinkFilter = activeLinkTypeFilter.size > 0;

    if (manualOnly) {
      nodes = nodes.filter((n) => n.group !== "manual" && !n.manual);
    }

    if (hideOrphans) {
      nodes = nodes.filter((n) => degreeMap[n.id] > 0);
    }

    if (activeTypeFilter.size > 0) {
      nodes = nodes.filter((n) => activeTypeFilter.has(n.icon || n.group));
    }

    if (hasLinkFilter) {
      const linkedNodeIds = new Set();

      (graphData.links || []).forEach((link) => {
        const label = link.label || "UNKNOWN";
        if (!activeLinkTypeFilter.has(label)) return;

        linkedNodeIds.add(getEndpointId(link.source));
        linkedNodeIds.add(getEndpointId(link.target));
      });

      nodes = nodes.filter((n) => linkedNodeIds.has(n.id));
    }

    return new Set(nodes.map((n) => n.id));
  }, [
    graphData,
    activeTypeFilter,
    activeLinkTypeFilter,
    manualOnly,
    hideOrphans,
    degreeMap,
  ]);

  const visibleLinkIds = useMemo(() => {
    if (!graphData?.links) return new Set();

    const ids = new Set();
    const hasLinkFilter = activeLinkTypeFilter.size > 0;

    graphData.links.forEach((link) => {
      const source = getEndpointId(link.source);
      const target = getEndpointId(link.target);
      const label = link.label || "UNKNOWN";

      const endpointsVisible =
        visibleNodeIds.has(source) && visibleNodeIds.has(target);
      const labelVisible = !hasLinkFilter || activeLinkTypeFilter.has(label);

      if (endpointsVisible && labelVisible) {
        ids.add(getLinkId(link));
      }
    });

    return ids;
  }, [graphData, visibleNodeIds, activeLinkTypeFilter]);

  const hasActiveFilter =
    activeTypeFilter.size > 0 ||
    activeLinkTypeFilter.size > 0 ||
    manualOnly ||
    hideOrphans ||
    degreeHighlight > 0 ||
    searchMatchedNodes.size > 0;

  const selectedDocumentIds = useMemo(() => {
    const ids = new Set();

    if (!graphData?.nodes) return ids;

    const selectedIds = new Set([
      ...Array.from(selectedNodeIdsRef.current || []),
      ...Array.from(selectedNodeIds || []),
    ]);

    graphData.nodes.forEach((node) => {
      if (!selectedIds.has(node.id)) return;
      const docId = node.document_id || node.doc_id || node.cardId;
      if (docId) ids.add(docId);
    });

    return ids;
  }, [graphData, selectedNodeIds]);

  const selectedNodes = useMemo(() => {
    if (!graphData?.nodes) return [];

    if (!selectedDocumentIds.size) {
      return graphData.nodes.filter((node) => selectedNodeIds.has(node.id));
    }

    return graphData.nodes.filter((node) => {
      const docId = node.document_id || node.doc_id || node.cardId;
      return (
        selectedNodeIds.has(node.id) ||
        (docId && selectedDocumentIds.has(docId))
      );
    });
  }, [graphData, selectedNodeIds, selectedDocumentIds]);

  const clearGraphSelection = useCallback(() => {
    selectedNodeIdsRef.current = new Set();
    setSelectedNodeIds(new Set());
  }, []);

  const removeSelectedNode = useCallback(
    (nodeId) => {
      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        const node = (graphData?.nodes || []).find((n) => n.id === nodeId);
        const docId = node?.document_id || node?.doc_id || node?.cardId;

        if (docId) {
          (graphData?.nodes || []).forEach((n) => {
            const nDocId = n?.document_id || n?.doc_id || n?.cardId;
            if (nDocId === docId) next.delete(n.id);
          });
        } else {
          next.delete(nodeId);
        }

        selectedNodeIdsRef.current = next;
        return next;
      });
    },
    [graphData],
  );

  const getNodeDeleteDocumentId = useCallback((node) => {
    return node?.document_id || node?.doc_id || node?.cardId || null;
  }, []);

  const removeNodesFromGraph = useCallback(
    (nodesToRemove) => {
      const removalDocIds = new Set();
      const removalNodeIds = new Set();

      (nodesToRemove || []).forEach((node) => {
        if (!node) return;
        const docId = node.document_id || node.doc_id || node.cardId;
        if (docId) removalDocIds.add(docId);
        if (node.id) removalNodeIds.add(node.id);
      });

      if (!removalDocIds.size && !removalNodeIds.size) return;

      setGraphData((prev) => {
        if (!prev) return prev;

        const removedNodeIds = new Set();

        (prev.nodes || []).forEach((node) => {
          const docId = node.document_id || node.doc_id || node.cardId;
          if (
            (docId && removalDocIds.has(docId)) ||
            removalNodeIds.has(node.id)
          ) {
            removedNodeIds.add(node.id);
          }
        });

        return {
          ...prev,
          nodes: (prev.nodes || []).filter(
            (node) => !removedNodeIds.has(node.id),
          ),
          links: (prev.links || []).filter((link) => {
            const source = getEndpointId(link.source);
            const target = getEndpointId(link.target);
            const linkDocId = link.document_id || link.doc_id || link.cardId;

            return (
              !(linkDocId && removalDocIds.has(linkDocId)) &&
              !removedNodeIds.has(source) &&
              !removedNodeIds.has(target)
            );
          }),
        };
      });

      setSelectedNodeIds((prev) => {
        const next = new Set(prev);
        (graphData?.nodes || []).forEach((node) => {
          const docId = node.document_id || node.doc_id || node.cardId;
          if (
            (docId && removalDocIds.has(docId)) ||
            removalNodeIds.has(node.id)
          ) {
            next.delete(node.id);
          }
        });
        selectedNodeIdsRef.current = next;
        return next;
      });

      const activeDocId = activeNodeRef.current
        ? activeNodeRef.current.document_id ||
          activeNodeRef.current.doc_id ||
          activeNodeRef.current.cardId
        : null;

      if (
        activeNodeRef.current &&
        ((activeDocId && removalDocIds.has(activeDocId)) ||
          removalNodeIds.has(activeNodeRef.current.id))
      ) {
        activeNodeRef.current = null;
        setActiveNode(null);
        setNodeMenu(null);
        setIsRightOpen(false);
        setRightComponent(null);
      }
    },
    [graphData, setIsRightOpen, setRightComponent],
  );

  const restorePendingDeletedGraphItems = useCallback((pending) => {
    if (!pending) return;

    const restoredNodeIds = new Set(
      (pending.nodes || []).map((node) => node.id).filter(Boolean),
    );
    const restoredLinkIds = new Set(
      (pending.links || []).map((link) => getLinkId(link)).filter(Boolean),
    );

    setGraphData((prev) => {
      if (!prev) {
        return {
          nodes: pending.nodes || [],
          links: pending.links || [],
        };
      }

      const currentNodeIds = new Set((prev.nodes || []).map((node) => node.id));
      const currentLinkIds = new Set(
        (prev.links || []).map((link) => getLinkId(link)),
      );

      return {
        ...prev,
        nodes: [
          ...(prev.nodes || []),
          ...(pending.nodes || []).filter(
            (node) => node?.id && !currentNodeIds.has(node.id),
          ),
        ],
        links: [
          ...(prev.links || []),
          ...(pending.links || []).filter(
            (link) => !currentLinkIds.has(getLinkId(link)),
          ),
        ],
      };
    });

    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      restoredNodeIds.forEach((nodeId) => next.add(nodeId));
      selectedNodeIdsRef.current = next;
      return next;
    });

    console.log("[MainGraph] restored pending deleted graph documents", {
      document_ids: pending.documentIds,
      node_ids: Array.from(restoredNodeIds),
      link_ids: Array.from(restoredLinkIds),
    });
  }, []);

  const commitPendingGraphDelete = useCallback(
    async (pendingId) => {
      const pending = pendingDeletePayloadsRef.current[pendingId];
      if (!pending) return;

      if (pendingDeleteTimersRef.current[pendingId]) {
        clearTimeout(pendingDeleteTimersRef.current[pendingId]);
        delete pendingDeleteTimersRef.current[pendingId];
      }

      delete pendingDeletePayloadsRef.current[pendingId];

      setToasts((prev) => prev.filter((toast) => toast.id !== pendingId));
      setIsDeletingSelection(true);

      try {
        if (!showTutorial) {
          await deleteGraphNodes({ document_ids: pending.documentIds });
        }

        console.log("[MainGraph] committed deferred graph document delete", {
          document_ids: pending.documentIds,
          node_count: pending.nodes?.length || 0,
          link_count: pending.links?.length || 0,
        });
      } catch (err) {
        console.error(
          "[MainGraph] deferred graph document delete failed:",
          err,
        );
        restorePendingDeletedGraphItems(pending);
        pushToast(
          "Delete failed — restored removed graph items",
          null,
          "selection",
        );
      } finally {
        setIsDeletingSelection(false);
      }
    },
    [pushToast, restorePendingDeletedGraphItems, showTutorial],
  );

  const schedulePendingGraphDelete = useCallback(
    (pending) => {
      const id = Date.now() + Math.random();

      pendingDeletePayloadsRef.current[id] = pending;

      setToasts((prev) => [
        ...prev,
        {
          id,
          type: "pending-delete-documents",
          message:
            pending.documentIds.length === 1
              ? "Document removed from graph"
              : `${pending.documentIds.length} documents removed from graph`,
          undoPayload: pending,
        },
      ]);

      pendingDeleteTimersRef.current[id] = setTimeout(() => {
        commitPendingGraphDelete(id);
      }, 10000);

      return id;
    },
    [commitPendingGraphDelete],
  );

  const handleDeleteSelectedNodes = useCallback(
    (nodesToDelete) => {
      const nodes = (nodesToDelete || []).filter(Boolean);

      if (!nodes.length) return;

      const documentIds = Array.from(
        new Set(nodes.map(getNodeDeleteDocumentId).filter(Boolean)),
      );

      if (!documentIds.length) {
        pushToast(
          "No document IDs found for selected nodes",
          null,
          "selection",
        );
        return;
      }

      const documentIdSet = new Set(documentIds);
      const nodeIdSet = new Set(nodes.map((node) => node.id).filter(Boolean));
      const graphNodes = graphData?.nodes || [];
      const graphLinks = graphData?.links || [];

      const nodesToRemove = graphNodes.filter((node) => {
        const docId = getNodeDeleteDocumentId(node);
        return (docId && documentIdSet.has(docId)) || nodeIdSet.has(node.id);
      });

      const removedNodeIds = new Set(
        nodesToRemove.map((node) => node.id).filter(Boolean),
      );

      const linksToRemove = graphLinks.filter((link) => {
        const source = getEndpointId(link.source);
        const target = getEndpointId(link.target);
        const linkDocId = link.document_id || link.doc_id || link.cardId;

        return (
          (linkDocId && documentIdSet.has(linkDocId)) ||
          removedNodeIds.has(source) ||
          removedNodeIds.has(target)
        );
      });

      console.log("[MainGraph] scheduled graph document delete", {
        document_ids: documentIds,
        nodes: nodesToRemove,
        links: linksToRemove,
      });

      removeNodesFromGraph(nodesToRemove.length ? nodesToRemove : nodes);

      schedulePendingGraphDelete({
        documentIds,
        nodes: nodesToRemove.length ? nodesToRemove : nodes,
        links: linksToRemove,
      });
    },
    [
      getNodeDeleteDocumentId,
      graphData,
      pushToast,
      removeNodesFromGraph,
      schedulePendingGraphDelete,
    ],
  );

  const handleDownloadSelection = useCallback(async () => {
    const selectedIds = new Set([
      ...Array.from(selectedNodeIdsRef.current || []),
      ...Array.from(selectedNodeIds || []),
    ]);

    const nodesById = new Map(
      (graphData?.nodes || []).filter(Boolean).map((node) => [node.id, node]),
    );

    const nodesFromSelectionIds = Array.from(selectedIds)
      .map((nodeId) => nodesById.get(nodeId))
      .filter(Boolean);

    const nodesForExport = nodesFromSelectionIds.length
      ? nodesFromSelectionIds
      : selectedNodes;

    const documentIds = Array.from(
      new Set(
        nodesForExport
          .map((node) => node?.document_id || node?.doc_id || node?.cardId)
          .filter(Boolean),
      ),
    );

    console.log("[MainGraph] export selected graph JSON", {
      selected_ids: Array.from(selectedIds),
      selected_nodes: nodesForExport,
      document_ids: documentIds,
    });

    if (!documentIds.length) {
      console.warn("[MainGraph] no document IDs found in selected nodes", {
        selected_ids: Array.from(selectedIds),
        selected_nodes: nodesForExport,
        selectedNodes,
        graph_nodes_sample: (graphData?.nodes || []).slice(0, 5),
      });
      return;
    }

    setIsDownloadingSelection(true);

    try {
      const payload = { document_ids: documentIds };
      const response = await exportGraphJSON(payload);
      const exportData = response?.data ?? response;
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `graph-selection-${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[MainGraph] download selected graph JSON failed:", err);
    } finally {
      setIsDownloadingSelection(false);
    }
  }, [graphData, selectedNodeIds, selectedNodes]);

  const linkTypes = useMemo(
    () =>
      connectedTypes.filter((ct) => ct.type === "link").map((ct) => ct.value),
    [connectedTypes],
  );

  const getNodeDocumentId = useCallback((node) => {
    return (
      node?.document_id ||
      node?.doc_id ||
      node?.documentId ||
      node?.cardId ||
      node?.card_id ||
      null
    );
  }, []);

  const normalizeDocIDResponse = useCallback((response) => {
    const payload = response?.data ?? response;

    if (typeof payload === "string") {
      try {
        return JSON.parse(payload);
      } catch {
        return payload;
      }
    }

    return payload;
  }, []);

  const openArticlePanel = useCallback(
    async (docId, fallbackArticle) => {
      if (!docId) return;

      const isDark = document.documentElement.classList.contains("dark");

      setIsRightOpen(true);
      setRightComponent(
        <ArticleDetailPanel
          key={`${docId}-loading`}
          article={fallbackArticle}
          cardId={docId}
          t={getTheme(isDark)}
        />,
      );

      try {
        const response = await getProductDocIdData(docId);
        const article = normalizeDocIDResponse(response) || fallbackArticle;

        setRightComponent(
          <ArticleDetailPanel
            key={docId}
            article={article}
            cardId={docId}
            t={getTheme(isDark)}
          />,
        );
      } catch (err) {
        console.error("[MainGraph] getDocIdData failed:", {
          docId,
          fallbackArticle,
          err,
        });

        setRightComponent(
          <ArticleDetailPanel
            key={`${docId}-fallback`}
            article={fallbackArticle}
            cardId={docId}
            t={getTheme(isDark)}
          />,
        );
      }
    },
    [normalizeDocIDResponse, setRightComponent, setIsRightOpen],
  );

  useEffect(() => {
    if (isRightOpen) return;

    activeNodeRef.current = null;
    setActiveNode(null);
    setNodeMenu(null);
  }, [isRightOpen]);

  const openConnectedNodeModalFromNode = useCallback(
    (node, screenX, screenY) => {
      if (!node) return;

      setContextMenu(null);
      setNodeMenu(null);
      setConnectionSourceNode(null);
      setNewNodeSourceNode(node);
      setShowNewNodeModal(true);
      setNewNodeModalPos({ x: screenX, y: screenY });
      setNewNodeForm({
        name: "",
        type: "PERSON",
        relationship: linkTypes[0] || "CONNECTED_TO",
        context: "",
        edgeContext: "",
        isPrivate: false,
      });
    },
    [linkTypes],
  );

  const startConnectionFromNode = useCallback((node) => {
    if (!node) return;

    setContextMenu(null);
    setNodeMenu(null);
    setPendingConnect(null);
    setConnectionSourceNode(node);
  }, []);

  const warmupTicks = useMemo(() => {
    const n = graphData?.nodes?.length || 0;
    return n > 500 ? 100 : n > 200 ? 150 : 200;
  }, [graphData]);

  const handleFgNodeClick = useCallback(
    (node, event) => {
      if (isDraggingFromPlus || suppressClickRef.current) return;

      const isSelectionClick = event?.metaKey || event?.ctrlKey;

      setContextMenu(null);

      if (isSelectionClick) {
        const nextNodes = new Set(selectedNodeIdsRef.current);
        const docId = node.document_id || node.doc_id || node.cardId;
        const clusterNodes = docId
          ? (graphData?.nodes || []).filter((n) => {
              const nDocId = n.document_id || n.doc_id || n.cardId;
              return nDocId === docId;
            })
          : [node];
        const clusterNodeIds = clusterNodes.map((n) => n.id).filter(Boolean);
        const wasSelected = clusterNodeIds.length
          ? clusterNodeIds.every((nodeId) => nextNodes.has(nodeId))
          : nextNodes.has(node.id);

        if (wasSelected) {
          clusterNodeIds.forEach((nodeId) => nextNodes.delete(nodeId));
          console.log("[MainGraph] deselected document cluster", {
            source: "cmd-click",
            node,
            document_id: docId,
            node_count: clusterNodes.length,
            nodes: clusterNodes,
          });
        } else {
          clusterNodeIds.forEach((nodeId) => nextNodes.add(nodeId));
          console.log("[MainGraph] selected document cluster", {
            source: "cmd-click",
            node,
            document_id: docId,
            node_count: clusterNodes.length,
            nodes: clusterNodes,
          });
          logSelectedGraphNode(node, "cmd-click");
        }

        selectedNodeIdsRef.current = nextNodes;
        setSelectedNodeIds(nextNodes);
        setNodeMenu(null);
        return;
      }

      if (connectionSourceNode) {
        if (connectionSourceNode.id === node.id) {
          setConnectionSourceNode(null);
          setNodeMenu(null);
          return;
        }

        setPendingConnect({
          source: connectionSourceNode,
          target: node,
          relationship: linkTypes[0] || "CONNECTED_TO",
          context: "",
          isPrivate: false,
          screenX: event?.clientX || window.innerWidth / 2,
          screenY: event?.clientY || window.innerHeight / 2,
        });
        setConnectionSourceNode(null);
        setNodeMenu(null);
        return;
      }

      activeNodeRef.current = node;
      setActiveNode(node);

      const docId = getNodeDocumentId(node);

      if (docId) {
        openArticlePanel(docId, node);
      } else {
        console.warn("[MainGraph] clicked node is missing a document ID", {
          node,
        });
      }

      setNodeMenu(null);
    },
    [
      graphData,
      isDraggingFromPlus,
      getNodeDocumentId,
      openArticlePanel,
      connectionSourceNode,
      linkTypes,
    ],
  );

  const handleFgNodeHover = useCallback((node) => {
    hoveredNodeRef.current = node || null;
    if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
    tooltipRafRef.current = requestAnimationFrame(() =>
      setTooltipNode(node || null),
    );
  }, []);

  const linkColorFn = useCallback(
    (link) => {
      const sId =
        typeof link.source === "object" ? link.source.id : link.source;
      const tId =
        typeof link.target === "object" ? link.target.id : link.target;
      const linkVisible =
        !hasActiveFilter ||
        (visibleNodeIds.has(sId) && visibleNodeIds.has(tId));
      if (!linkVisible)
        return dark ? "rgba(14,165,233,0.03)" : "rgba(0,0,0,0.03)";
      const hNode = hoveredNodeRef.current;
      if (
        hNode &&
        (link.source.name === hNode.name || link.target.name === hNode.name)
      )
        return t.link.hovered;
      if (searchMatchedLinks.size > 0 && searchMatchedLinks.has(link.id))
        return "rgba(14,165,233,0.65)";
      return t.link.default;
    },
    [dark, hasActiveFilter, visibleNodeIds, searchMatchedLinks, t.link],
  );

  const linkWidthFn = useCallback(
    (link) => {
      const sId =
        typeof link.source === "object" ? link.source.id : link.source;
      const tId =
        typeof link.target === "object" ? link.target.id : link.target;
      const linkVisible =
        !hasActiveFilter ||
        (visibleNodeIds.has(sId) && visibleNodeIds.has(tId));
      return linkVisible
        ? searchMatchedLinks.size > 0 && searchMatchedLinks.has(link.id)
          ? 3
          : 2
        : 0.3;
    },
    [hasActiveFilter, visibleNodeIds, searchMatchedLinks],
  );

  const handleZoomToFit = () => {
    if (fgRef.current) fgRef.current.zoomToFit(0, 60);
  };

  const handleRelayout = () => {
    if (!graphData?.nodes) return;
    graphData.nodes.forEach((n) => {
      n.fx = undefined;
      n.fy = undefined;
      if (is3D) n.fz = undefined;
    });
    setStopEngine(false);
    setGraphData({ ...graphData });
    setTimeout(() => {
      if (fgRef.current) fgRef.current.d3ReheatSimulation();
    }, 50);
  };

  const focusGraphNode = useCallback(
    (node, options = {}) => {
      if (!node) return;

      const { delay = 80, duration = 650, zoom = 3.35 } = options;

      window.setTimeout(() => {
        if (!fgRef.current) return;

        const x = node.x ?? node.fx ?? 0;
        const y = node.y ?? node.fy ?? 0;
        const z = node.z ?? node.fz ?? 0;

        if (is3D) {
          fgRef.current.cameraPosition?.(
            { x, y, z: z + 150 },
            { x, y, z },
            duration,
          );
        } else {
          fgRef.current.centerAt?.(x, y, duration);
          fgRef.current.zoom?.(zoom, duration);
        }

        activeNodeRef.current = node;
        setActiveNode(node);
      }, delay);
    },
    [is3D],
  );

  const handleZoomToNode = (nodeId) => {
    const node = graphData?.nodes?.find((n) => n.id === nodeId);
    if (node) focusGraphNode(node, { delay: 0, duration: 600, zoom: 4 });
  };

  const clearAllFilters = useCallback(() => {
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    setActiveTypeFilter(new Set());
    setActiveLinkTypeFilter(new Set());
    setManualOnly(false);
    setHideOrphans(false);
    setDegreeHighlight(0);
  }, []);

  const switchToMyGraph = () => {
    setActiveGraphUuid(null);
    setGraphSourceLabel("My Graph");
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "my_graph" };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "my_graph" },
      }),
    );
    getGraphEdges(graphLimit)
      .then((r) => {
        try {
          createGraph(r.data);
        } catch (e) {
          console.error("[MainGraph] switchToMyGraph createGraph failed:", e);
        }
      })
      .catch((err) => {
        console.error("[MainGraph] switchToMyGraph getGraphEdges failed:", err);
        setIsLoading(false);
      });
  };

  const switchToTeamGraph = (member) => {
    setActiveGraphUuid(member.uuid);
    setGraphSourceLabel(`${member.first_name} ${member.last_name}'s Graph`);
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "team", uuid: member.uuid };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "team", uuid: member.uuid },
      }),
    );
    getTeamMemberGraph(member.uuid, graphLimit)
      .then((r) => {
        try {
          createGraph(r.data);
        } catch (e) {
          console.error("[MainGraph] switchToTeamGraph createGraph failed:", e);
        }
      })
      .catch((err) => {
        console.error("[MainGraph] switchToTeamGraph failed:", err);
        setIsLoading(false);
      });
  };

  const switchToGlobalGraph = () => {
    setActiveGraphUuid(null);
    setGraphSourceLabel("Global");
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: "global" };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: "global" },
      }),
    );
    getGlobalGraph(graphLimit)
      .then((r) => {
        try {
          createGraph(r.data);
        } catch (e) {
          console.error(
            "[MainGraph] switchToGlobalGraph createGraph failed:",
            e,
          );
        }
      })
      .catch((err) => {
        console.error("[MainGraph] switchToGlobalGraph failed:", err);
        setIsLoading(false);
      });
  };

  const handleControlsSelect = (item) => {
    setStopEngine(false);
    needsZoomToFit.current = true;
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    graphSourceMeta.current = { kind: item.type, value: item.value };
    fetchBackendCounts();
    window.dispatchEvent(
      new CustomEvent("graph:active-source-changed", {
        detail: { kind: item.type, value: item.value },
      }),
    );
    const fetchFn =
      item.type === "node"
        ? getConnectedNodeByType(item.value, graphLimit)
        : getConnectedEdgeByType(item.value, graphLimit);
    fetchFn
      .then((r) => {
        try {
          if (r.data?.status !== 501) {
            setGraphSourceLabel(item.value.replace(/_/g, " ").toLowerCase());
            createGraph(r.data);
          }
        } catch (e) {
          console.error(
            "[MainGraph] handleControlsSelect createGraph failed:",
            e,
          );
        }
      })
      .catch((err) => {
        console.error("[MainGraph] handleControlsSelect fetch failed:", err);
        setIsLoading(false);
      });
  };

  // FIX: added fetchBackendCounts to the dependency array
  const applyCustomView = useCallback(
    (view, overrideLimit) => {
      const { node_types, edge_types, keywords, sources, target_types } =
        view.filters || {};
      setGraphSourceLabel(view.name);
      setStopEngine(false);
      needsZoomToFit.current = true;
      setBarSearch("");
      setSearchMatchedNodes(new Set());
      setSearchMatchedLinks(new Set());
      graphSourceMeta.current = { kind: "custom_view", view };
      fetchBackendCounts();
      window.dispatchEvent(
        new CustomEvent("graph:active-source-changed", {
          detail: { kind: "custom_view", viewId: view.document_id },
        }),
      );
      getFilteredGraphView({
        filters: {
          node_types,
          edge_types,
          keywords,
          sources,
          target_types,
          date_from: graphDateFrom,
          date_to: graphDateTo,
        },
        limit: overrideLimit ?? graphLimit,
      })
        .then((r) => {
          try {
            createGraph(r.data);
          } catch (e) {
            console.error("[MainGraph] applyCustomView createGraph failed:", e);
          }
        })
        .catch((err) => {
          console.error("[MainGraph] applyCustomView fetch failed:", err);
          setIsLoading(false);
        });
    },
    [graphLimit, graphDateFrom, graphDateTo, fetchBackendCounts],
  );

  const isFavorited = useCallback(
    (kind, value, teamUuid) =>
      graphFavorites.some(
        (f) =>
          f.favorite_kind === kind &&
          f.favorite_value === value &&
          (kind !== "team" || f.favorite_meta?.team_uuid === teamUuid),
      ),
    [graphFavorites],
  );

  const getFavoriteId = useCallback(
    (kind, value, teamUuid) => {
      const fav = graphFavorites.find(
        (f) =>
          f.favorite_kind === kind &&
          f.favorite_value === value &&
          (kind !== "team" || f.favorite_meta?.team_uuid === teamUuid),
      );
      return fav?.document_id || null;
    },
    [graphFavorites],
  );

  const toggleFavorite = useCallback(
    async (kind, value, label, meta = {}) => {
      const favId = getFavoriteId(kind, value, meta?.team_uuid);
      try {
        if (favId) await unfavoriteGraphView({ document_id: favId });
        else await favoriteGraphView({ kind, value, label, meta });
        loadGraphViewsData();
        window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
      } catch (err) {
        console.error("[MainGraph] toggleFavorite failed:", err);
      }
    },
    [getFavoriteId, loadGraphViewsData],
  );

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      if (detail.kind === "my_graph") switchToMyGraph();
      else if (detail.kind === "global") switchToGlobalGraph();
      else if (detail.kind === "team") {
        const teamUuid =
          detail.uuid ||
          detail.user_uuid ||
          detail.userUuid ||
          detail.team_uuid ||
          detail.value ||
          detail.meta?.team_uuid ||
          detail.meta?.uuid ||
          detail.member?.uuid ||
          detail.member?.user_uuid;

        const teamName =
          detail.meta?.team_name ||
          detail.team_name ||
          detail.label?.replace(/'s Graph$/i, "") ||
          detail.member?.name ||
          `${detail.member?.first_name || ""} ${detail.member?.last_name || ""}`.trim();

        const nameParts = String(teamName || "")
          .trim()
          .split(/\s+/);

        if (!teamUuid) {
          console.warn(
            "[MainGraph] Missing team uuid for graph switch",
            detail,
          );
          return;
        }

        switchToTeamGraph({
          ...(detail.member || {}),
          uuid: teamUuid,
          first_name: detail.member?.first_name || nameParts[0] || "Team",
          last_name:
            detail.member?.last_name || nameParts.slice(1).join(" ") || "Graph",
        });
      } else if (detail.kind === "node_type" || detail.kind === "node")
        handleControlsSelect({ type: "node", value: detail.value });
      else if (
        detail.kind === "edge_type" ||
        detail.kind === "edge" ||
        detail.kind === "link"
      )
        handleControlsSelect({ type: "link", value: detail.value });
      else if (detail.kind === "custom_view" && detail.view)
        applyCustomView(detail.view);
    };
    window.addEventListener("sidebar:graph-switch-source", handler);
    return () =>
      window.removeEventListener("sidebar:graph-switch-source", handler);
  }, [applyCustomView, companyKey]);

  const refetchWithLimit = (newLimit) => {
    const meta = graphSourceMeta.current;
    setStopEngine(false);
    needsZoomToFit.current = true;
    if (meta.kind === "custom_view" && meta.view) {
      applyCustomView(meta.view, newLimit);
      return;
    }
    if (graphDateFrom || graphDateTo) {
      getFilteredGraphView({
        filters: {
          node_types: [],
          edge_types: [],
          keywords: [],
          sources: [],
          target_types: [],
          date_from: graphDateFrom,
          date_to: graphDateTo,
        },
        limit: newLimit,
      })
        .then((r) => {
          try {
            createGraph(r.data);
          } catch (e) {
            console.error(
              "[MainGraph] refetchWithLimit (date) createGraph failed:",
              e,
            );
          }
        })
        .catch((err) => {
          console.error(
            "[MainGraph] refetchWithLimit (date) fetch failed:",
            err,
          );
          setIsLoading(false);
        });
      return;
    }
    let fetchFn;
    if (meta.kind === "global") fetchFn = getGlobalGraph(newLimit);
    else if (meta.kind === "team")
      fetchFn = getTeamMemberGraph(meta.uuid, newLimit);
    else if (meta.kind === "node")
      fetchFn = getConnectedNodeByType(meta.value, newLimit);
    else if (meta.kind === "link")
      fetchFn = getConnectedEdgeByType(meta.value, newLimit);
    else fetchFn = getGraphEdges(newLimit);
    fetchFn
      .then((r) => {
        try {
          createGraph(r.data);
        } catch (e) {
          console.error("[MainGraph] refetchWithLimit createGraph failed:", e);
        }
      })
      .catch((err) => {
        console.error("[MainGraph] refetchWithLimit fetch failed:", err);
        setIsLoading(false);
      });
  };

  const executeSearch = useCallback(
    (query, onResults) => {
      if (!query || query.trim().length < 2) {
        setSearchMatchedNodes(new Set());
        setSearchMatchedLinks(new Set());
        if (query.trim().length === 0) refetchWithLimit(graphLimit);
        if (onResults) onResults([]);
        return;
      }
      const meta = graphSourceMeta.current;
      let scope = "";
      let scopeValue = "";
      let userUuid = "";
      if (meta.kind === "global") scope = "global";
      else if (meta.kind === "team") {
        scope = "team";
        userUuid = meta.uuid;
      } else if (meta.kind === "node") {
        scope = "node_type";
        scopeValue = meta.value;
      } else if (meta.kind === "link") {
        scope = "edge_type";
        scopeValue = meta.value;
      }
      searchGraph(query.trim(), graphLimit, scope, userUuid, scopeValue)
        .then((r) => {
          try {
            const data = r.data;
            if (data?.status === 200) {
              setStopEngine(false);
              needsZoomToFit.current = true;
              createGraph(data);
              setSearchMatchedNodes(new Set(data.matched_nodes || []));
              setSearchMatchedLinks(new Set(data.matched_links || []));
              const matchedSet = new Set(data.matched_nodes || []);
              const preview = (data.nodes || [])
                .filter((n) => matchedSet.has(n.name))
                .slice(0, 12);
              if (onResults) onResults(preview);
            } else {
              if (onResults) onResults([]);
            }
          } catch (e) {
            console.error("[MainGraph] executeSearch createGraph failed:", e);
            if (onResults) onResults([]);
          }
        })
        .catch((err) => {
          console.error("[MainGraph] searchGraph failed:", err);
          if (onResults) onResults([]);
        });
    },
    [graphLimit],
  );

  const handleBarSearchChange = useCallback((value) => setBarSearch(value), []);
  const handleClearSearch = useCallback(() => {
    setBarSearch("");
    setSearchMatchedNodes(new Set());
    setSearchMatchedLinks(new Set());
    refetchWithLimit(graphLimit);
  }, [graphLimit]);

  // FIX: guard against the effect firing on initial mount (when both dates
  // are still ""), which would race against the primary companyKey effect.
  useEffect(() => {
    if (!isMountedFetchRef.current) return;
    refetchWithLimit(graphLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphDateFrom, graphDateTo]);

  const handleUndo = useCallback(
    (toast) => {
      const skipApi = showTutorial;
      if (toast.type === "pending-delete-documents") {
        if (pendingDeleteTimersRef.current[toast.id]) {
          clearTimeout(pendingDeleteTimersRef.current[toast.id]);
          delete pendingDeleteTimersRef.current[toast.id];
        }

        const pending =
          pendingDeletePayloadsRef.current[toast.id] || toast.undoPayload;
        delete pendingDeletePayloadsRef.current[toast.id];
        restorePendingDeletedGraphItems(pending);
        dismissToast(toast.id);
        return;
      }

      if (toast.type === "connect-existing") {
        setConnectionSourceNode(null);
        setPendingConnect(null);
        setGraphData((prev) => ({
          nodes: prev.nodes.map((n) => {
            if (n.x != null && n.fx == null) {
              n.fx = n.x;
              n.fy = n.y;
              if (is3D) n.fz = n.z;
            }
            return n;
          }),
          links: prev.links.filter((l) => {
            if (toast.undoPayload.edgeId)
              return l.id !== toast.undoPayload.edgeId;

            return !(
              (l.source === toast.undoPayload.source ||
                l.source?.id === toast.undoPayload.source) &&
              (l.target === toast.undoPayload.target ||
                l.target?.id === toast.undoPayload.target)
            );
          }),
        }));
        if (!skipApi)
          deleteEdge({
            source: toast.undoPayload.source,
            target: toast.undoPayload.target,
          }).catch(console.error);
      } else if (toast.type === "create-and-connect") {
        const nodeName = toast.undoPayload.newNode.name;
        setGraphData((prev) => ({
          nodes: prev.nodes
            .filter((n) => n.name !== nodeName)
            .map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
          links: prev.links.filter((l) => {
            const s = typeof l.source === "object" ? l.source.name : l.source;
            const t = typeof l.target === "object" ? l.target.name : l.target;
            return !(s === nodeName || t === nodeName);
          }),
        }));
        if (!skipApi) deleteNode({ name: nodeName }).catch(console.error);
      } else if (toast.type === "create-standalone") {
        const nodeName = toast.undoPayload.newNode.name;
        setGraphData((prev) => ({
          nodes: prev.nodes
            .filter((n) => n.name !== nodeName)
            .map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
          links: prev.links,
        }));
        if (!skipApi) deleteNode({ name: nodeName }).catch(console.error);
      } else if (toast.type === "delete-node") {
        const { node: savedNode, edges: savedEdges } = toast.undoPayload;
        setGraphData((prev) => ({
          nodes: [
            ...prev.nodes.map((n) => {
              if (n.x != null && n.fx == null) {
                n.fx = n.x;
                n.fy = n.y;
                if (is3D) n.fz = n.z;
              }
              return n;
            }),
            savedNode,
          ],
          links: [...prev.links, ...savedEdges],
        }));
        if (!skipApi) {
          createNode({
            name: savedNode.name,
            type: savedNode.icon || "PERSON",
            context: savedNode.context || "restored",
            isPrivate: savedNode.isPrivate !== false,
          })
            .then((res) => {
              const newDocId = res.data.node.document_id;
              setGraphData((prev) => ({
                ...prev,
                nodes: prev.nodes.map((n) =>
                  n.name === savedNode.name && !n.document_id
                    ? { ...n, document_id: newDocId }
                    : n,
                ),
              }));
              savedEdges.forEach((e) => {
                const src =
                  typeof e.source === "object" ? e.source.id : e.source;
                const tgt =
                  typeof e.target === "object" ? e.target.id : e.target;
                const otherName = src === savedNode.name ? tgt : src;
                setGraphData((prev) => {
                  const otherNode = prev.nodes.find((n) => n.id === otherName);
                  if (otherNode?.document_id) {
                    createEdge({
                      source:
                        src === savedNode.name
                          ? newDocId
                          : otherNode.document_id,
                      target:
                        tgt === savedNode.name
                          ? newDocId
                          : otherNode.document_id,
                      source_name: src,
                      source_type:
                        src === savedNode.name
                          ? savedNode.icon || "PERSON"
                          : otherNode.icon || "PERSON",
                      target_name: tgt,
                      target_type:
                        tgt === savedNode.name
                          ? savedNode.icon || "PERSON"
                          : otherNode.icon || "PERSON",
                      relationship: e.label || "CONNECTED_TO",
                      context: e.context || "restored",
                      isPrivate: e.isPrivate !== false,
                    }).catch(console.error);
                  }
                  return prev;
                });
              });
            })
            .catch(console.error);
        }
      } else if (toast.type === "delete-edge") {
        const { edge: savedEdge } = toast.undoPayload;
        setGraphData((prev) => {
          const src =
            typeof savedEdge.source === "object"
              ? savedEdge.source.id
              : savedEdge.source;
          const tgt =
            typeof savedEdge.target === "object"
              ? savedEdge.target.id
              : savedEdge.target;
          const srcNode = prev.nodes.find((n) => n.id === src);
          const tgtNode = prev.nodes.find((n) => n.id === tgt);
          if (srcNode?.document_id && tgtNode?.document_id && !skipApi) {
            createEdge({
              source: srcNode.document_id,
              target: tgtNode.document_id,
              source_name: src,
              source_type: srcNode.icon || "PERSON",
              target_name: tgt,
              target_type: tgtNode.icon || "PERSON",
              relationship: savedEdge.label || "CONNECTED_TO",
              context: savedEdge.context || "restored",
              isPrivate: savedEdge.isPrivate !== false,
            }).catch(console.error);
          }
          return { ...prev, links: [...prev.links, savedEdge] };
        });
      }
      dismissToast(toast.id);
    },
    [dismissToast, restorePendingDeletedGraphItems, showTutorial, is3D],
  );

  useEffect(() => {
    if (!isRightOpen) {
      activeNodeRef.current = null;
      setActiveNode(null);
    }
  }, [isRightOpen]);
  useEffect(() => {
    return () => {
      Object.values(toastTimersRef.current).forEach(clearTimeout);
      Object.values(pendingDeleteTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const measure = () => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setDimensions({
      width: Math.max(0, Math.floor(rect.width)),
      height: Math.max(0, Math.floor(rect.height)),
    });
  };

  useLayoutEffect(() => {
    measure();
    if (!divRef.current) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(divRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    measure();
    const t1 = setTimeout(measure, 120),
      t2 = setTimeout(measure, 260),
      t3 = setTimeout(measure, 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [isLeftOpen, isRightOpen, rightWidth, LEFT_WIDTH]);

  useEffect(() => {
    if (!fgRef.current) return;
    fgRef.current.d3ReheatSimulation?.();
    if (!isLeftOpen && !isRightOpen)
      requestAnimationFrame(() => fgRef.current?.d3ReheatSimulation?.());
  }, [dimensions.width, dimensions.height, isLeftOpen, isRightOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (divRef.current)
        setDimensions({
          width: divRef.current.offsetWidth,
          height: divRef.current.offsetHeight,
        });
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function createGraph(data) {
    try {
      const d = typeof data === "string" ? JSON.parse(data) : data;
      const nodesRaw = Array.isArray(d?.nodes) ? d.nodes : [];
      const linksRaw = Array.isArray(d?.links) ? d.links : [];

      if (nodesRaw.length === 0 && linksRaw.length === 0) {
        setIsGraphEmpty(true);
        setIsLoading(false);
        return;
      }

      setIsGraphEmpty(false);

      const nodesByKey = new Map();
      const addNodeAlias = (map, key, node) => {
        if (key !== null && key !== undefined && String(key).trim() !== "") {
          map.set(String(key), node);
        }
      };

      const makeGraphNode = (item) => {
        const id = item.id || item.document_id || item.name;
        const name = item.name || item.id || item.document_id;

        return {
          ...item,
          id,
          name,
          group: item.group,
          icon: item.icon,
          iconType: resolveIconType(item.icon),
          img: safeIcon(item.icon),
          document_id: item.document_id,
          description: item.description || item.context || null,
          context: item.context || "",
          manual: item.manual || item.group === "manual",
          isPrivate: item.isPrivate,
        };
      };

      const nodes_arr = nodesRaw.map(makeGraphNode);
      const temp = nodes_arr.reduce((acc, item) => {
        if (!acc[item.id]) acc[item.id] = item;

        addNodeAlias(nodesByKey, item.id, item);
        addNodeAlias(nodesByKey, item.name, item);
        addNodeAlias(nodesByKey, item.document_id, item);

        return acc;
      }, {});

      const resolveNodeId = (endpoint) => {
        const endpointId = getEndpointId(endpoint);
        if (endpointId === null || endpointId === undefined) return endpointId;

        const key = String(endpointId);
        const matchedNode = nodesByKey.get(key);
        return matchedNode?.id || endpointId;
      };

      linksRaw.forEach((l) => {
        const rawSource = getEndpointId(l.source);
        const rawTarget = getEndpointId(l.target);
        const sourceId = resolveNodeId(l.source);
        const targetId = resolveNodeId(l.target);

        if (sourceId && !temp[sourceId]) {
          temp[sourceId] = {
            id: sourceId,
            name: rawSource || sourceId,
            group: "orphan",
            icon: "PERSON",
            iconType: "PERSON",
            img: safeIcon("PERSON"),
            document_id: l.document_id || null,
          };
          addNodeAlias(nodesByKey, sourceId, temp[sourceId]);
          addNodeAlias(nodesByKey, rawSource, temp[sourceId]);
        }

        if (targetId && !temp[targetId]) {
          temp[targetId] = {
            id: targetId,
            name: rawTarget || targetId,
            group: "orphan",
            icon: "PERSON",
            iconType: "PERSON",
            img: safeIcon("PERSON"),
            document_id: l.document_id || null,
          };
          addNodeAlias(nodesByKey, targetId, temp[targetId]);
          addNodeAlias(nodesByKey, rawTarget, temp[targetId]);
        }
      });

      const links = linksRaw
        .map((l, idx) => {
          const source = resolveNodeId(l.source);
          const target = resolveNodeId(l.target);

          if (!source || !target) return null;

          return {
            ...l,
            id:
              l.id ||
              l.document_id ||
              `${source || "source"}-${target || "target"}-${l.label || "edge"}-${idx}`,
            source,
            target,
            value: l.value === null || l.value === undefined ? 1 : l.value,
          };
        })
        .filter(Boolean);

      selectedNodeIdsRef.current = new Set();
      setSelectedNodeIds(new Set());
      setGraphData({ nodes: Object.values(temp), links });
      setIsLoading(false);

      setTimeout(() => {
        if (fgRef.current) fgRef.current.zoomToFit?.(400, 60);
      }, 300);
    } catch (e) {
      console.error("[MainGraph] createGraph failed to parse response:", e);
      setIsLoading(false);
    }
  }

  // FIX: primary data-fetching effect — now the only thing that kicks off
  // the initial load. After it completes we flip isMountedFetchRef so the
  // date-range effect is free to fire on subsequent changes.
  useEffect(() => {
    setConnectedTypes([]);
    setFilteredResults([]);
    setSearchTitle(null);
    setIsGraphEmpty(false);
    setIsLoading(true);
    isMountedFetchRef.current = false;

    getAllConnectedNodeEdgesTypes()
      .then((response) => {
        try {
          const linkResults = response.data.link_types.map((item) => ({
            type: "link",
            value: item,
          }));
          const nodeResults = response.data.nodes_types.map((item) => ({
            type: "node",
            value: item,
          }));
          setConnectedTypes((prev) => {
            const combined = [...prev, ...linkResults, ...nodeResults];
            const unique = Array.from(
              new Map(combined.map((i) => [i.value, i])).values(),
            );
            return unique.sort((a, b) => a.value.localeCompare(b.value));
          });
        } catch (e) {
          console.error(
            "[MainGraph] getAllConnectedNodeEdgesTypes parse failed:",
            e,
          );
        }
      })
      .catch((err) => {
        console.error("[MainGraph] getAllConnectedNodeEdgesTypes failed:", err);
      });

    let pendingGraphSourceSwitch = null;

    try {
      pendingGraphSourceSwitch =
        window.__pendingGraphSourceSwitch ||
        JSON.parse(
          sessionStorage.getItem("pendingGraphSourceSwitch") || "null",
        );

      window.__pendingGraphSourceSwitch = null;
      sessionStorage.removeItem("pendingGraphSourceSwitch");
    } catch {
      pendingGraphSourceSwitch = window.__pendingGraphSourceSwitch || null;
      window.__pendingGraphSourceSwitch = null;
    }

    if (pendingGraphSourceSwitch?.kind) {
      console.log(
        "[MainGraph] consuming pending graph source switch",
        pendingGraphSourceSwitch,
      );

      // Prevent the initial My Graph fetch from racing and overwriting the
      // source the user clicked in the sidebar. The event listener above is
      // mounted before this effect runs, so this dispatch is received by
      // MainGraph and routed through the same switch path as normal clicks.
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("sidebar:graph-switch-source", {
            detail: pendingGraphSourceSwitch,
          }),
        );
      }, 0);

      isMountedFetchRef.current = true;
      return;
    }

    getGraphEdges(graphLimit)
      .then((response) => {
        try {
          createGraph(response.data);
        } catch (e) {
          console.error(
            "[MainGraph] initial getGraphEdges createGraph failed:",
            e,
          );
          setIsLoading(false);
        }
      })
      .catch((err) => {
        // FIX: was silently swallowed — now logged and loading state cleared
        console.error("[MainGraph] initial getGraphEdges failed:", err);
        setIsLoading(false);
      })
      .finally(() => {
        // Allow the date-range effect to fire from this point forward
        isMountedFetchRef.current = true;
      });

    fetchBackendCounts();
  }, [companyKey]);

  useEffect(() => {
    setFilteredResults(
      searchEntry !== ""
        ? connectedTypes.filter((i) =>
            i.value.toLowerCase().includes(searchEntry.toLowerCase()),
          )
        : connectedTypes,
    );
  }, [searchEntry, connectedTypes]);

  const handleCreateNewNode = () => {
    if (!newNodeForm.name.trim() || !newNodeSourceNode) return;
    const icon = safeIcon(newNodeForm.type);
    const tempId = `__temp_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: newNodeForm.name,
      group: "manual",
      icon: newNodeForm.type,
      img: icon,
      document_id: null,
      context: newNodeForm.context,
      isPrivate: newNodeForm.isPrivate,
    };
    const canvasRect = divRef.current
      ?.querySelector("canvas")
      ?.getBoundingClientRect();
    if (canvasRect && fgRef.current) {
      if (is3D) {
        const srcNode = newNodeSourceNode;
        const offset = 50 + Math.random() * 30;
        const angle = Math.random() * Math.PI * 2;
        const phi = Math.random() * Math.PI;
        newNode.x = (srcNode.x || 0) + Math.sin(phi) * Math.cos(angle) * offset;
        newNode.y = (srcNode.y || 0) + Math.sin(phi) * Math.sin(angle) * offset;
        newNode.z = (srcNode.z || 0) + Math.cos(phi) * offset;
        newNode.fx = newNode.x;
        newNode.fy = newNode.y;
        newNode.fz = newNode.z;
      } else {
        const gPos = fgRef.current.screen2GraphCoords(
          newNodeModalPos.x - canvasRect.left,
          newNodeModalPos.y - canvasRect.top,
        );
        newNode.x = gPos.x;
        newNode.y = gPos.y;
        newNode.fx = gPos.x;
        newNode.fy = gPos.y;
      }
    }
    const sourceId = newNodeSourceNode.id;
    const sourceName = newNodeSourceNode.name;
    const sourceDocId = newNodeSourceNode.document_id;
    setGraphData((prev) => ({
      nodes: [
        ...prev.nodes.map((n) => {
          if (n.x != null && n.fx == null) {
            n.fx = n.x;
            n.fy = n.y;
            if (is3D) n.fz = n.z;
          }
          return n;
        }),
        newNode,
      ],
      links: [
        ...prev.links,
        {
          source: sourceId,
          target: tempId,
          label: newNodeForm.relationship || "CONNECTED_TO",
          manual: true,
        },
      ],
    }));
    pushToast(
      `Created "${newNodeForm.name}" and connected from "${sourceName}"`,
      {
        newNode: { id: tempId, name: newNode.name, type: newNodeForm.type },
        edge: { source: sourceId, target: tempId },
      },
      "create-and-connect",
    );
    focusGraphNode(newNode, { delay: 90, duration: 700, zoom: 3.35 });
    setShowNewNodeModal(false);
    setNewNodeSourceNode(null);
    if (showTutorial) return;
    createNode({
      name: newNodeForm.name,
      type: newNodeForm.type,
      context: newNodeForm.context,
      isPrivate: newNodeForm.isPrivate,
    })
      .then((res) => {
        const created = res.data.node;
        const focusedNode = {
          ...newNode,
          id: created.id,
          document_id: created.document_id,
        };

        activeNodeRef.current = focusedNode;
        setActiveNode(focusedNode);

        setGraphData((prev) => ({
          nodes: prev.nodes.map((n) =>
            n.id === tempId
              ? { ...n, id: created.id, document_id: created.document_id }
              : n,
          ),
          links: prev.links.map((l) => ({
            ...l,
            source:
              l.source === tempId || l.source?.id === tempId
                ? created.id
                : l.source,
            target:
              l.target === tempId || l.target?.id === tempId
                ? created.id
                : l.target,
          })),
        }));
        return createEdge({
          source: sourceDocId,
          target: created.document_id,
          source_name: sourceName,
          source_type: newNodeSourceNode.icon || "PERSON",
          target_name: newNodeForm.name,
          target_type: newNodeForm.type,
          relationship: newNodeForm.relationship || "CONNECTED_TO",
          context: newNodeForm.edgeContext,
          isPrivate: newNodeForm.isPrivate,
        });
      })
      .then((edgeRes) => {
        if (edgeRes?.data?.edge?.document_id) {
          const {
            document_id: edgeDocId,
            source: edgeSrc,
            target: edgeTgt,
          } = edgeRes.data.edge;
          setGraphData((prev) => ({
            ...prev,
            links: prev.links.map((l) => {
              const lSrc =
                typeof l.source === "object" ? l.source.id : l.source;
              const lTgt =
                typeof l.target === "object" ? l.target.id : l.target;
              return lSrc === edgeSrc && lTgt === edgeTgt && !l.document_id
                ? { ...l, document_id: edgeDocId }
                : l;
            }),
          }));
        }
      })
      .catch((err) => {
        console.error("[MainGraph] Create node + edge failed:", err);
        setGraphData((prev) => ({
          nodes: prev.nodes.filter((n) => n.id !== tempId),
          links: prev.links.filter(
            (l) =>
              !(
                l.source === tempId ||
                l.source?.id === tempId ||
                l.target === tempId ||
                l.target?.id === tempId
              ),
          ),
        }));
      });
  };

  const handleCreateStandaloneNode = () => {
    if (!standaloneForm.name.trim()) return;
    const icon = safeIcon(standaloneForm.type);
    const tempId = `__temp_${Date.now()}`;
    const newNode = {
      id: tempId,
      name: standaloneForm.name,
      group: "manual",
      icon: standaloneForm.type,
      img: icon,
      document_id: null,
      context: standaloneForm.context,
      isPrivate: standaloneForm.isPrivate,
    };
    if (standaloneGraphPosRef.current) {
      newNode.x = standaloneGraphPosRef.current.x;
      newNode.y = standaloneGraphPosRef.current.y;
      if (is3D) newNode.z = standaloneGraphPosRef.current.z || 0;
      newNode.fx = newNode.x;
      newNode.fy = newNode.y;
      if (is3D) newNode.fz = newNode.z;
    } else {
      newNode.x = (Math.random() - 0.5) * 40;
      newNode.y = (Math.random() - 0.5) * 40;
      if (is3D) newNode.z = (Math.random() - 0.5) * 40;
      newNode.fx = newNode.x;
      newNode.fy = newNode.y;
      if (is3D) newNode.fz = newNode.z;
    }
    setGraphData((prev) => ({
      nodes: [
        ...prev.nodes.map((n) => {
          if (n.x != null && n.fx == null) {
            n.fx = n.x;
            n.fy = n.y;
            if (is3D) n.fz = n.z;
          }
          return n;
        }),
        newNode,
      ],
      links: prev.links,
    }));
    pushToast(
      `Created standalone node "${standaloneForm.name}"`,
      {
        newNode: { id: tempId, name: newNode.name, type: standaloneForm.type },
      },
      "create-standalone",
    );
    focusGraphNode(newNode, { delay: 90, duration: 700, zoom: 3.35 });
    setShowStandaloneModal(false);
    setStandaloneForm({
      name: "",
      type: "PERSON",
      context: "",
      isPrivate: false,
    });
    if (showTutorial) return;
    createNode({
      name: standaloneForm.name,
      type: standaloneForm.type,
      context: standaloneForm.context,
      isPrivate: standaloneForm.isPrivate,
    })
      .then((res) => {
        const created = res.data.node;
        const focusedNode = {
          ...newNode,
          id: created.id,
          document_id: created.document_id,
        };

        activeNodeRef.current = focusedNode;
        setActiveNode(focusedNode);

        setGraphData((prev) => ({
          ...prev,
          nodes: prev.nodes.map((n) =>
            n.id === tempId
              ? { ...n, id: created.id, document_id: created.document_id }
              : n,
          ),
        }));
      })
      .catch((err) => {
        console.error("[MainGraph] Create standalone node failed:", err);
        setGraphData((prev) => ({
          ...prev,
          nodes: prev.nodes.filter((n) => n.id !== tempId),
        }));
      });
  };

  const handleConfirmConnect = () => {
    if (!pendingConnect) return;

    const { source, target, relationship } = pendingConnect;
    const tempEdgeId = `manual-edge-${source.id}-${target.id}-${Date.now()}`;
    const context = pendingConnect.context;
    const isPrivate = pendingConnect.isPrivate;

    setConnectionSourceNode(null);
    setPendingConnect(null);
    setContextMenu(null);
    setNodeMenu(null);

    setGraphData((prev) => ({
      nodes: prev.nodes.map((n) => {
        if (n.x != null && n.fx == null) {
          n.fx = n.x;
          n.fy = n.y;
          if (is3D) n.fz = n.z;
        }
        return n;
      }),
      links: [
        ...prev.links,
        {
          id: tempEdgeId,
          source: source.id,
          target: target.id,
          label: relationship,
          manual: true,
          context,
          isPrivate,
        },
      ],
    }));

    pushToast(
      `Connected "${source.name}" → "${target.name}"`,
      { source: source.id, target: target.id, edgeId: tempEdgeId },
      "connect-existing",
    );

    if (!showTutorial) {
      createEdge({
        source: source.document_id,
        target: target.document_id,
        source_name: source.name,
        source_type: source.icon || "PERSON",
        target_name: target.name,
        target_type: target.icon || "PERSON",
        relationship,
        context,
        isPrivate,
      })
        .then((res) => {
          const edgeDocId = res.data.edge?.document_id;
          if (edgeDocId) {
            setGraphData((prev) => ({
              ...prev,
              links: prev.links.map((l) =>
                l.id === tempEdgeId ? { ...l, document_id: edgeDocId } : l,
              ),
            }));
          }
        })
        .catch((err) => {
          console.error("[MainGraph] Create edge failed:", err);
          setGraphData((prev) => ({
            ...prev,
            links: prev.links.filter((l) => l.id !== tempEdgeId),
          }));
        });
    }
  };

  const openEditModal = (item, kind, sx, sy) => {
    setContextMenu(null);
    if (kind === "node") {
      setEditModal({
        item,
        kind,
        form: {
          name: item.name,
          type: item.icon || item.group || "PERSON",
          context: item.context || "",
          isPrivate: item.isPrivate !== false,
        },
        screenX: sx,
        screenY: sy,
      });
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      setEditModal({
        item,
        kind,
        form: {
          source: src,
          target: tgt,
          relationship: item.label || "CONNECTED_TO",
          context: item.context || "",
          isPrivate: item.isPrivate !== false,
        },
        screenX: sx,
        screenY: sy,
      });
    }
  };

  const openDeleteModal = (item, kind, sx, sy) => {
    setContextMenu(null);
    setDeleteModal({ item, kind, screenX: sx, screenY: sy });
  };

  const handleEditConfirm = () => {
    if (!editModal) return;
    const { item, kind, form } = editModal;
    if (kind === "node") {
      const oldName = item.name;
      const newName = form.name.trim();
      const newType = form.type;
      if (!newName) return;
      setGraphData((prev) => ({
        nodes: prev.nodes.map((n) =>
          n.id === item.id
            ? {
                ...n,
                id: newName,
                name: newName,
                icon: newType,
                group: "manual",
                context: form.context,
                isPrivate: form.isPrivate,
              }
            : n,
        ),
        links: prev.links.map((l) => ({
          ...l,
          source:
            l.source === oldName || l.source?.id === oldName
              ? newName
              : l.source,
          target:
            l.target === oldName || l.target?.id === oldName
              ? newName
              : l.target,
        })),
      }));
      if (!showTutorial)
        updateNode({
          old_name: oldName,
          name: newName,
          type: newType,
          context: form.context,
          isPrivate: form.isPrivate,
        }).catch(console.error);
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      setGraphData((prev) => ({
        ...prev,
        links: prev.links.map((l) => {
          const lSrc = typeof l.source === "object" ? l.source.id : l.source;
          const lTgt = typeof l.target === "object" ? l.target.id : l.target;
          return lSrc === src && lTgt === tgt && l.manual
            ? {
                ...l,
                label: form.relationship,
                context: form.context,
                isPrivate: form.isPrivate,
              }
            : l;
        }),
      }));
      if (!showTutorial)
        updateEdge({
          source: src,
          target: tgt,
          relationship: form.relationship,
          context: form.context,
          isPrivate: form.isPrivate,
        }).catch(console.error);
    }
    setEditModal(null);
  };

  const handleDeleteConfirm = () => {
    if (!deleteModal) return;
    const { item, kind } = deleteModal;
    if (kind === "node") {
      const nodeName = item.name;
      const savedNode = graphData.nodes.find((n) => n.id === item.id);
      const savedEdges = graphData.links.filter((l) => {
        const src = typeof l.source === "object" ? l.source.id : l.source;
        const tgt = typeof l.target === "object" ? l.target.id : l.target;
        return l.manual && (src === nodeName || tgt === nodeName);
      });
      setGraphData((prev) => ({
        nodes: prev.nodes.filter((n) => n.id !== item.id),
        links: prev.links.filter((l) => {
          const src = typeof l.source === "object" ? l.source.id : l.source;
          const tgt = typeof l.target === "object" ? l.target.id : l.target;
          return !(l.manual && (src === nodeName || tgt === nodeName));
        }),
      }));
      pushToast(
        `Deleted node "${nodeName}"`,
        { node: savedNode, edges: savedEdges },
        "delete-node",
      );
      if (!showTutorial) deleteNode({ name: nodeName }).catch(console.error);
    } else {
      const src =
        typeof item.source === "object" ? item.source.id : item.source;
      const tgt =
        typeof item.target === "object" ? item.target.id : item.target;
      const savedEdge = graphData.links.find((l) => {
        const lSrc = typeof l.source === "object" ? l.source.id : l.source;
        const lTgt = typeof l.target === "object" ? l.target.id : l.target;
        return lSrc === src && lTgt === tgt && l.manual;
      });
      setGraphData((prev) => ({
        ...prev,
        links: prev.links.filter((l) => {
          const lSrc = typeof l.source === "object" ? l.source.id : l.source;
          const lTgt = typeof l.target === "object" ? l.target.id : l.target;
          return !(lSrc === src && lTgt === tgt && l.manual);
        }),
      }));
      pushToast(
        `Deleted edge "${src}" → "${tgt}"`,
        { edge: savedEdge },
        "delete-edge",
      );
      if (!showTutorial)
        deleteEdge({ source: src, target: tgt }).catch(console.error);
    }
    setDeleteModal(null);
  };

  const getDragLineCoords = () => {
    if (!connectDrag?.sourceNode || !fgRef.current || !divRef.current)
      return null;
    const canvasRect = divRef.current
      ?.querySelector("canvas")
      ?.getBoundingClientRect();
    const wrapperRect = divRef.current.getBoundingClientRect();
    if (!canvasRect) return null;
    const { sourceNode, currentScreenPos } = connectDrag;
    const ss = fgRef.current.graph2ScreenCoords(
      sourceNode.x,
      sourceNode.y,
      sourceNode.z || 0,
    );
    const cx = ss.x + (canvasRect.left - wrapperRect.left);
    const cy = ss.y + (canvasRect.top - wrapperRect.top);
    const ex = currentScreenPos.x - wrapperRect.left;
    const ey = currentScreenPos.y - wrapperRect.top;
    const dx = ex - cx,
      dy = ey - cy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    let edgeOffset;
    if (is3D && fgRef.current?.camera) {
      const p = fgRef.current.camera().position;
      const cdx = p.x - (sourceNode.x || 0),
        cdy = p.y - (sourceNode.y || 0),
        cdz = p.z - (sourceNode.z || 0);
      edgeOffset = Math.max(
        8,
        (CIRCLE_RADIUS * 0.72 * 800) /
          Math.sqrt(cdx * cdx + cdy * cdy + cdz * cdz),
      );
    } else {
      edgeOffset = CIRCLE_RADIUS * zoomRef.current + 4;
    }
    const x1 = cx + (dx / dist) * edgeOffset,
      y1 = cy + (dy / dist) * edgeOffset;
    let x2 = ex,
      y2 = ey;
    if (connectTargetNode) {
      const ts = fgRef.current.graph2ScreenCoords(
        connectTargetNode.x,
        connectTargetNode.y,
        connectTargetNode.z || 0,
      );
      const tx = ts.x + (canvasRect.left - wrapperRect.left);
      const ty = ts.y + (canvasRect.top - wrapperRect.top);
      const tdx = tx - x1,
        tdy = ty - y1;
      const tdist = Math.sqrt(tdx * tdx + tdy * tdy) || 1;
      x2 = tx - (tdx / tdist) * edgeOffset;
      y2 = ty - (tdy / tdist) * edgeOffset;
    }
    return { x1, y1, x2, y2 };
  };
  const dragLine = getDragLineCoords();

  const truncate = (str, max = 40) =>
    !str ? "" : str.length > max ? str.slice(0, max) + "…" : str;
  const formatType = (iconName) =>
    !iconName
      ? ""
      : iconName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const rendererProps = {
    fgRef,
    divRef,
    hoveredNodeRef,
    selectedNodeIdsRef,
    connectDragRef,
    selectionBoxRef,
    lastDragPosRef,
    suppressClickRef,
    cursorGraphPosRef,
    tooltipRafRef,
    zoomRef,
    needsZoomToFit,
    standaloneGraphPosRef,
    standaloneScreenPosRef,
    iconImgCacheRef,
    graphData,
    setGraphData,
    isDraggingFromPlus,
    setIsDraggingFromPlus,
    stopEngine,
    setStopEngine,
    dimensions,
    isLoading,
    isGraphEmpty,
    setTooltipNode,
    setConnectDrag,
    setSelectionBox,
    setConnectTargetNode,
    setNodeMenu,
    setContextMenu,
    setActiveNode: (n) => {
      activeNodeRef.current = n;
      setActiveNode(n);
    },
    setPendingConnect,
    setShowNewNodeModal,
    setNewNodeModalPos,
    setNewNodeForm,
    setNewNodeSourceNode,
    setShowStandaloneModal,
    setStandaloneForm,
    setSelectedNodeIds,
    hasActiveFilter,
    visibleNodeIds,
    visibleLinkIds,
    activeLinkTypeFilter,
    degreeMap,
    degreeHighlight,
    searchMatchedNodes,
    searchMatchedLinks,
    nodeMenu,
    connectTargetNode,
    t,
    dark,
    showEdgeLabels,
    warmupTicks,
    linkTypes,
    handleFgNodeClick,
    handleFgNodeHover,
    linkColorFn,
    linkWidthFn,
    openEditModal,
    openDeleteModal,
    activeNodeRef,
  };

  return (
    <div
      ref={divRef}
      className="relative flex flex-col w-full h-full overflow-hidden rounded-md"
    >
      {/* ── Toasts ── */}
      {toasts.length > 0 && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 pointer-events-none">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className="pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-lg backdrop-blur-md shadow-lg animate-[slideDown_0.3s_ease-out] relative"
              style={{
                background: t.surface.bg,
                border: `1px solid ${t.surface.border}`,
                boxShadow: t.surface.toastShadow,
              }}
            >
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full ${t.check.bg} border ${t.check.border} flex items-center justify-center`}
              >
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  className={t.check.icon}
                >
                  <path
                    d="M2 5.5L4 7.5L8 3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="min-w-0">
                <span
                  className={`${t.text.primary} block text-xs font-light whitespace-nowrap max-w-[320px] truncate`}
                >
                  {toast.message}
                </span>
                {toast.type === "pending-delete-documents" && (
                  <span
                    className={`${t.text.muted} block text-[10px] leading-tight whitespace-nowrap max-w-[320px] truncate`}
                  >
                    Undo within 10 seconds before the delete request is sent.
                  </span>
                )}
              </div>
              <button
                className="flex-shrink-0 ml-1 px-2.5 py-1 rounded text-[11px] font-medium text-sky-500 hover:text-white bg-sky-500/10 hover:bg-sky-500/30 border border-sky-500/20 hover:border-sky-500/40 transition-all duration-150"
                onClick={() => handleUndo(toast)}
              >
                Undo
              </button>
              {toast.type !== "pending-delete-documents" && (
                <button
                  className={`flex-shrink-0 ${t.btn.dismissIcon} transition-colors`}
                  onClick={() => dismissToast(toast.id)}
                >
                  {React.createElement(X, { size: 13 })}
                </button>
              )}
              <div
                className={`absolute bottom-0 left-0 h-[2px] ${t.progressBar} rounded-b-lg`}
                style={{
                  animation: "shrinkWidth 10s linear forwards",
                  width: "100%",
                }}
              />
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideDown { from { opacity: 0; transform: translateY(-12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes shrinkWidth { from { width: 100%; } to { width: 0%; } }
        @keyframes spin { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }
        .type-pills-scroll::-webkit-scrollbar { display: none; }
        .type-pills-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="range"] { -webkit-appearance: none; appearance: none; height: 4px; border-radius: 2px; background: ${dark ? "rgba(14,165,233,0.15)" : "rgba(100,116,139,0.2)"}; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 12px; height: 12px; border-radius: 50%; background: rgba(14,165,233,0.8); cursor: pointer; }
        ${PANEL_KEYFRAMES}
      `}</style>

      {/* ── Floating modals ── */}
      {showStandaloneModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Add New Node"
          initialX={standaloneScreenPosRef.current.x}
          initialY={standaloneScreenPosRef.current.y}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.secondary} text-xs`}>
              This node will be added without any connections.
            </span>
          </div>
          <ModalInput
            t={t}
            label="Node Name"
            placeholder="Enter node name..."
            value={standaloneForm.name}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, name: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateStandaloneNode();
              if (e.key === "Escape") setShowStandaloneModal(false);
            }}
          />
          <ModalDropdown
            t={t}
            label="Node Type"
            value={standaloneForm.type}
            options={NODE_TYPE_OPTIONS}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, type: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={standaloneForm.context}
            onChange={(e) =>
              setStandaloneForm((f) => ({ ...f, context: e.target.value }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={standaloneForm.isPrivate}
            onChange={(v) => setStandaloneForm((f) => ({ ...f, isPrivate: v }))}
          />
          <ModalActions
            t={t}
            onCancel={() => setShowStandaloneModal(false)}
            onConfirm={handleCreateStandaloneNode}
            confirmLabel="Add Node"
            disabled={
              !standaloneForm.name.trim() || !standaloneForm.context.trim()
            }
          />
        </FloatingPanel>
      )}

      {pendingConnect && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Connect Nodes"
          initialX={pendingConnect.screenX}
          initialY={pendingConnect.screenY}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.accent} text-xs font-medium`}>
              {pendingConnect.source.name}
            </span>
            <span className={`${t.text.secondary} text-xs`}> → </span>
            <span className={`${t.text.accent} text-xs font-medium`}>
              {pendingConnect.target.name}
            </span>
          </div>
          <ModalDropdown
            t={t}
            label="Relationship"
            value={pendingConnect.relationship}
            options={linkTypes}
            onChange={(e) =>
              setPendingConnect((prev) => ({
                ...prev,
                relationship: e.target.value,
              }))
            }
          />
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={pendingConnect.context}
            onChange={(e) =>
              setPendingConnect((prev) => ({
                ...prev,
                context: e.target.value,
              }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={pendingConnect.isPrivate}
            onChange={(v) =>
              setPendingConnect((prev) => ({ ...prev, isPrivate: v }))
            }
          />
          <ModalActions
            t={t}
            onCancel={() => {
              setPendingConnect(null);
              setConnectionSourceNode(null);
            }}
            onConfirm={handleConfirmConnect}
            confirmLabel="Connect"
            disabled={
              !pendingConnect.relationship || !pendingConnect.context?.trim()
            }
          />
        </FloatingPanel>
      )}

      {contextMenu?.kind === "node" && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(null)}
          />
          <div
            className="fixed z-50 rounded-lg shadow-lg backdrop-blur-md py-1 min-w-[140px]"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
              background: t.surface.popupBg,
              border: `1px solid ${t.surface.border}`,
              boxShadow: t.surface.popupShadow,
            }}
          >
            {contextMenu.kind === "node" && (
              <>
                <button
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${t.text.primary}`}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.surface.activeRow;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                  onClick={() =>
                    openConnectedNodeModalFromNode(
                      contextMenu.item,
                      contextMenu.x,
                      contextMenu.y,
                    )
                  }
                >
                  <Plus size={12} style={{ opacity: 0.75 }} />
                  Add Connected Node
                </button>

                <button
                  className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors ${t.text.primary}`}
                  style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = t.surface.activeRow;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                  onClick={() => startConnectionFromNode(contextMenu.item)}
                >
                  <Link2 size={12} style={{ opacity: 0.75 }} />
                  Connect to Another Node
                </button>
              </>
            )}
          </div>
        </>
      )}

      {editModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title={editModal.kind === "node" ? "Edit Node" : "Edit Edge"}
          initialX={editModal.screenX}
          initialY={editModal.screenY}
        >
          {editModal.kind === "node" ? (
            <>
              <ModalInput
                t={t}
                label="Node Name"
                placeholder="Enter node name..."
                value={editModal.form.name}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, name: e.target.value },
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleEditConfirm();
                  if (e.key === "Escape") setEditModal(null);
                }}
              />
              <ModalDropdown
                t={t}
                label="Node Type"
                value={editModal.form.type}
                options={NODE_TYPE_OPTIONS}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, type: e.target.value },
                  }))
                }
              />
            </>
          ) : (
            <>
              <div className="mb-3">
                <label
                  className={`block ${t.text.secondary} text-xs font-medium mb-1`}
                >
                  Source
                </label>
                <div
                  className={`text-xs px-3 py-2 rounded ${t.input.bg} border ${t.input.border} ${t.text.primary}`}
                >
                  {editModal.form.source}
                </div>
              </div>
              <div className="mb-3">
                <label
                  className={`block ${t.text.secondary} text-xs font-medium mb-1`}
                >
                  Target
                </label>
                <div
                  className={`text-xs px-3 py-2 rounded ${t.input.bg} border ${t.input.border} ${t.text.primary}`}
                >
                  {editModal.form.target}
                </div>
              </div>
              <ModalDropdown
                t={t}
                label="Relationship"
                value={editModal.form.relationship}
                options={linkTypes}
                onChange={(e) =>
                  setEditModal((prev) => ({
                    ...prev,
                    form: { ...prev.form, relationship: e.target.value },
                  }))
                }
              />
            </>
          )}
          <ModalTextarea
            t={t}
            label="Context"
            placeholder="Add description or notes..."
            value={editModal.form.context}
            onChange={(e) =>
              setEditModal((prev) => ({
                ...prev,
                form: { ...prev.form, context: e.target.value },
              }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={editModal.form.isPrivate}
            onChange={(v) =>
              setEditModal((prev) => ({
                ...prev,
                form: { ...prev.form, isPrivate: v },
              }))
            }
          />
          <ModalActions
            t={t}
            onCancel={() => setEditModal(null)}
            onConfirm={handleEditConfirm}
            confirmLabel="Save Changes"
            disabled={
              editModal.kind === "node"
                ? !editModal.form.name.trim() || !editModal.form.context?.trim()
                : !editModal.form.relationship.trim() ||
                  !editModal.form.context?.trim()
            }
          />
        </FloatingPanel>
      )}

      {deleteModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title={deleteModal.kind === "node" ? "Delete Node" : "Delete Edge"}
          initialX={deleteModal.screenX}
          initialY={deleteModal.screenY}
        >
          <div
            className={`mb-3 px-3 py-3 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            {deleteModal.kind === "node" ? (
              <span className={`${t.text.secondary} text-xs`}>
                Are you sure you want to delete{" "}
                <strong className={t.text.primary}>
                  {deleteModal.item.name}
                </strong>
                ? This will also remove any manually created edges connected to
                this node.
              </span>
            ) : (
              <span className={`${t.text.secondary} text-xs`}>
                Are you sure you want to delete the edge between{" "}
                <strong className={t.text.primary}>
                  {typeof deleteModal.item.source === "object"
                    ? deleteModal.item.source.id
                    : deleteModal.item.source}
                </strong>
                {" → "}
                <strong className={t.text.primary}>
                  {typeof deleteModal.item.target === "object"
                    ? deleteModal.item.target.id
                    : deleteModal.item.target}
                </strong>
                ?
              </span>
            )}
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              className={`px-4 py-1.5 rounded text-xs font-medium border ${t.btn.secondary} transition-colors`}
              onClick={() => setDeleteModal(null)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-1.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              onClick={handleDeleteConfirm}
            >
              Delete
            </button>
          </div>
        </FloatingPanel>
      )}

      {showNewNodeModal && (
        <FloatingPanel
          t={t}
          dark={dark}
          title="Create New Node"
          initialX={newNodeModalPos.x}
          initialY={newNodeModalPos.y}
        >
          <div
            className={`mb-3 px-3 py-2 rounded ${t.badge.bg} border ${t.badge.border}`}
          >
            <span className={`${t.text.secondary} text-xs`}>
              Connecting from:{" "}
            </span>
            <span className={`${t.text.accent} text-xs font-medium`}>
              {newNodeSourceNode?.name}
            </span>
          </div>
          <ModalInput
            t={t}
            label="Node Name"
            placeholder="Enter node name..."
            value={newNodeForm.name}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, name: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateNewNode();
              if (e.key === "Escape") {
                setShowNewNodeModal(false);
                setNewNodeSourceNode(null);
              }
            }}
          />
          <ModalDropdown
            t={t}
            label="Node Type"
            value={newNodeForm.type}
            options={NODE_TYPE_OPTIONS}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, type: e.target.value }))
            }
          />
          <ModalDropdown
            t={t}
            label="Relationship"
            value={newNodeForm.relationship}
            options={linkTypes}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, relationship: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Node Context"
            placeholder="Describe this node..."
            value={newNodeForm.context}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, context: e.target.value }))
            }
          />
          <ModalTextarea
            t={t}
            label="Edge Context"
            placeholder="Describe this connection..."
            value={newNodeForm.edgeContext}
            onChange={(e) =>
              setNewNodeForm((f) => ({ ...f, edgeContext: e.target.value }))
            }
          />
          <ModalToggle
            t={t}
            label="Private"
            checked={newNodeForm.isPrivate}
            onChange={(v) => setNewNodeForm((f) => ({ ...f, isPrivate: v }))}
          />
          <ModalActions
            t={t}
            onCancel={() => {
              setShowNewNodeModal(false);
              setNewNodeSourceNode(null);
            }}
            onConfirm={handleCreateNewNode}
            confirmLabel="Create & Connect"
            disabled={
              !newNodeForm.name.trim() ||
              !newNodeForm.context.trim() ||
              !newNodeForm.edgeContext.trim()
            }
          />
        </FloatingPanel>
      )}

      {/* ── Main content ── */}
      {isGraphEmpty ? (
        <div className="relative z-10 flex w-full h-full items-center justify-center">
          <div className={`${t.empty} text-sm font-extralight`}>
            No graph data available
          </div>
        </div>
      ) : (
        <>
          {isLoading ? (
            <div className="relative z-10 flex w-full h-full items-center justify-center">
              {/* <LoadingSpinner />*/}
            </div>
          ) : (
            <>
              {connectionSourceNode && (
                <div
                  className="pointer-events-auto absolute left-1/2 top-[76px] z-30 -translate-x-1/2 rounded-xl px-3 py-2 text-xs backdrop-blur-xl"
                  style={{
                    background: dark
                      ? "rgba(10,16,28,0.88)"
                      : "rgba(255,255,255,0.90)",
                    border: dark
                      ? "1px solid rgba(14,165,233,0.18)"
                      : "1px solid rgba(14,165,233,0.16)",
                    color: dark
                      ? "rgba(226,232,240,0.90)"
                      : "rgba(15,23,42,0.88)",
                    boxShadow: dark
                      ? "0 18px 48px rgba(0,0,0,0.26)"
                      : "0 18px 42px rgba(15,23,42,0.10)",
                  }}
                >
                  Connecting from "{connectionSourceNode.name}" — click another
                  node, or press Escape to cancel.
                </div>
              )}

              {/* ── Legend panel (top-right) ── */}
              <AnimatePresence>
                <LegendPanel
                  dark={dark}
                  t={t}
                  typeCounts={typeCounts}
                  linkTypeCounts={linkTypeCounts}
                  activeTypeFilter={activeTypeFilter}
                  setActiveTypeFilter={setActiveTypeFilter}
                  activeLinkTypeFilter={activeLinkTypeFilter}
                  setActiveLinkTypeFilter={setActiveLinkTypeFilter}
                />
              </AnimatePresence>

              <AnimatePresence>
                {selectedNodes.length > 0 && (
                  <SelectedItemsPanel
                    dark={dark}
                    t={t}
                    selectedNodes={selectedNodes}
                    onRemoveNode={removeSelectedNode}
                    onClearSelection={clearGraphSelection}
                    onDownloadSelection={handleDownloadSelection}
                    isDownloadingSelection={isDownloadingSelection}
                    onDeleteNode={(node) => handleDeleteSelectedNodes([node])}
                    onDeleteSelected={() =>
                      handleDeleteSelectedNodes(selectedNodes)
                    }
                    isDeletingSelection={isDeletingSelection}
                  />
                )}
              </AnimatePresence>

              {/* ── SVG overlay: drag line ── */}
              {connectDrag && dragLine && (
                <svg
                  className="absolute inset-0 z-30 pointer-events-none"
                  width="100%"
                  height="100%"
                >
                  <defs>
                    <linearGradient
                      id="connect-line-grad"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        stopColor={t.dragLine.from}
                        stopOpacity={t.dragLine.fromOpacity}
                      />
                      <stop
                        offset="100%"
                        stopColor={t.dragLine.to}
                        stopOpacity={t.dragLine.toOpacity}
                      />
                    </linearGradient>
                  </defs>
                  <line
                    x1={dragLine.x1}
                    y1={dragLine.y1}
                    x2={dragLine.x2}
                    y2={dragLine.y2}
                    stroke="url(#connect-line-grad)"
                    strokeWidth="2"
                    strokeDasharray="6 3"
                    strokeLinecap="round"
                  />
                  {!connectTargetNode &&
                    (() => {
                      const gr = is3D
                        ? 14
                        : Math.max(CIRCLE_RADIUS * zoomRef.current * 0.8, 12);
                      const cx = dragLine.x2,
                        cy = dragLine.y2;
                      return (
                        <>
                          <circle
                            cx={cx}
                            cy={cy}
                            r={gr}
                            fill={
                              dark
                                ? "rgba(15,23,42,0.5)"
                                : "rgba(255,255,255,0.5)"
                            }
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.35)"
                                : "rgba(100,116,139,0.30)"
                            }
                            strokeWidth="1.5"
                            strokeDasharray="4 3"
                          />
                          <line
                            x1={cx - gr * 0.35}
                            y1={cy}
                            x2={cx + gr * 0.35}
                            y2={cy}
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <line
                            x1={cx}
                            y1={cy - gr * 0.35}
                            x2={cx}
                            y2={cy + gr * 0.35}
                            stroke={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                          <text
                            x={cx}
                            y={cy + gr + 12}
                            textAnchor="middle"
                            fill={
                              dark
                                ? "rgba(148,163,184,0.5)"
                                : "rgba(100,116,139,0.45)"
                            }
                            fontSize="10"
                            fontFamily="poppins"
                          >
                            New node
                          </text>
                        </>
                      );
                    })()}
                  {connectTargetNode &&
                    (() => {
                      const canvasRect = divRef.current
                        ?.querySelector("canvas")
                        ?.getBoundingClientRect();
                      const wrapperRect =
                        divRef.current?.getBoundingClientRect();
                      if (!canvasRect || !wrapperRect || !fgRef.current)
                        return null;
                      const ts = fgRef.current.graph2ScreenCoords(
                        connectTargetNode.x,
                        connectTargetNode.y,
                        connectTargetNode.z || 0,
                      );
                      const tx = ts.x + (canvasRect.left - wrapperRect.left);
                      const ty = ts.y + (canvasRect.top - wrapperRect.top);
                      let r;
                      if (is3D && fgRef.current?.camera) {
                        const p = fgRef.current.camera().position;
                        const cdx = p.x - (connectTargetNode.x || 0),
                          cdy = p.y - (connectTargetNode.y || 0),
                          cdz = p.z - (connectTargetNode.z || 0);
                        r =
                          Math.max(
                            8,
                            (CIRCLE_RADIUS * 0.72 * 800) /
                              Math.sqrt(cdx * cdx + cdy * cdy + cdz * cdz),
                          ) + 6;
                      } else {
                        r = CIRCLE_RADIUS * zoomRef.current + 6;
                      }
                      return (
                        <>
                          <circle
                            cx={tx}
                            cy={ty}
                            r={r + 4}
                            fill="none"
                            stroke="rgba(14,165,233,0.25)"
                            strokeWidth="2"
                          >
                            <animate
                              attributeName="r"
                              values={`${r + 2};${r + 8};${r + 2}`}
                              dur="1.2s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.6;0.2;0.6"
                              dur="1.2s"
                              repeatCount="indefinite"
                            />
                          </circle>
                          <circle
                            cx={tx}
                            cy={ty}
                            r={r}
                            fill="none"
                            stroke="rgba(14,165,233,0.5)"
                            strokeWidth="2"
                            strokeDasharray="4 3"
                          />
                          <circle
                            cx={tx}
                            cy={ty}
                            r={4}
                            fill="rgba(14,165,233,0.8)"
                          />
                        </>
                      );
                    })()}
                </svg>
              )}

              {/* ── SVG overlay: selection box ── */}
              {selectionBox &&
                selectionBox.moved &&
                (() => {
                  const wr = divRef.current?.getBoundingClientRect();
                  if (!wr) return null;
                  const x =
                    Math.min(selectionBox.startX, selectionBox.currentX) -
                    wr.left;
                  const y =
                    Math.min(selectionBox.startY, selectionBox.currentY) -
                    wr.top;
                  const w = Math.abs(
                    selectionBox.currentX - selectionBox.startX,
                  );
                  const h = Math.abs(
                    selectionBox.currentY - selectionBox.startY,
                  );
                  return (
                    <svg
                      className="absolute inset-0 z-30 pointer-events-none"
                      width="100%"
                      height="100%"
                    >
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill={t.surface.selectionBoxFill}
                        stroke={t.surface.selectionBoxStroke}
                        strokeWidth="1"
                        strokeDasharray="4 2"
                        rx="2"
                      />
                    </svg>
                  );
                })()}

              {/* ── New-styled 2D renderer with old graph behavior wired in ── */}
              <motion.div
                className="w-full h-full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <VisualGraphRenderer2D {...rendererProps} />
              </motion.div>
            </>
          )}
        </>
      )}

      {/* ── Bottom control bar ── */}
      <AnimatePresence>
        <GraphBottomBar
          t={t}
          dark={dark}
          datePickerTheme={datePickerTheme}
          is3D={false}
          setIs3D={() => {}}
          graphSourceLabel={graphSourceLabel}
          graphSourceMeta={graphSourceMeta}
          activeGraphUuid={activeGraphUuid}
          connectedTypes={connectedTypes}
          teamMembers={teamMembers}
          customViews={customViews}
          switchToMyGraph={switchToMyGraph}
          switchToTeamGraph={switchToTeamGraph}
          switchToGlobalGraph={switchToGlobalGraph}
          handleControlsSelect={handleControlsSelect}
          applyCustomView={applyCustomView}
          isFavorited={isFavorited}
          toggleFavorite={toggleFavorite}
          deleteGraphView={deleteGraphView}
          loadGraphViewsData={loadGraphViewsData}
          barSearch={barSearch}
          onSearchChange={handleBarSearchChange}
          onExecuteSearch={executeSearch}
          onClearSearch={handleClearSearch}
          onClearAllFilters={clearAllFilters}
          searchMatchedNodes={searchMatchedNodes}
          showEdgeLabels={showEdgeLabels}
          setShowEdgeLabels={setShowEdgeLabels}
          manualOnly={manualOnly}
          setManualOnly={setManualOnly}
          hideOrphans={hideOrphans}
          setHideOrphans={setHideOrphans}
          degreeHighlight={degreeHighlight}
          setDegreeHighlight={setDegreeHighlight}
          graphDateFrom={graphDateFrom}
          graphDateTo={graphDateTo}
          setGraphDateFrom={setGraphDateFrom}
          setGraphDateTo={setGraphDateTo}
          graphLimit={graphLimit}
          setGraphLimit={setGraphLimit}
          refetchWithLimit={refetchWithLimit}
          graphStats={graphStats}
          backendTotals={backendTotals}
          handleZoomToFit={handleZoomToFit}
          handleRelayout={handleRelayout}
          handleZoomToNode={handleZoomToNode}
          onOpenCreateView={() => {
            setEditingView(null);
            setShowCreateViewModal(true);
          }}
          onOpenEditView={(view) => {
            setEditingView(view);
            setShowCreateViewModal(true);
          }}
        />
      </AnimatePresence>

      {showTutorial && (
        <GraphTutorial
          dark={dark}
          onClose={() => {
            setShowTutorial(false);
            setTutorialStep(0);
            if (tutorialGraphSnapshotRef.current) {
              setGraphData(tutorialGraphSnapshotRef.current);
              tutorialGraphSnapshotRef.current = null;
            }
          }}
          onStepChange={setTutorialStep}
          graphState={{
            hoveredNode: hoveredNodeRef.current,
            selectedNodeIds,
            showStandaloneModal,
            showNewNodeModal,
            connectDrag,
            showEdgeLabels,
            manualOnly,
            hideOrphans,
            degreeHighlight,
            activeTypeFilter,
            barSearch,
            graphSourceOpen: false,
            graphLimit,
            zoomLevel: zoomRef.current,
            nodeCount: graphData?.nodes?.length || 0,
            linkCount: graphData?.links?.length || 0,
          }}
        />
      )}

      {showCreateViewModal && (
        <CreateEditViewModal
          t={t}
          dark={dark}
          initialView={editingView}
          connectedTypes={connectedTypes}
          graphSources={graphSources}
          graphTargetTypes={graphTargetTypes}
          onClose={() => {
            setShowCreateViewModal(false);
            setEditingView(null);
          }}
          onCreated={applyCustomView}
          loadGraphViewsData={loadGraphViewsData}
        />
      )}
    </div>
  );
};

export default MainGraph;
